import type { CommentsProvider } from "@framework/comments/comments.types";
import type { SiteSettingsOverride } from "@framework/global-data/GlobalDataContainer";
import { type CommentLocation } from "@voxmedia/duet-context/context/config-context";
import { type NavItem } from "@voxmedia/duet-context/context/global-data-context";

import { SB_NATION_COMMUNITY_ID } from "./communities.sbnation";

export type { CommentsProvider };

export type FeedOnlyConfig = {
  mainNav?: Array<NavItem>;
  footerNav?: Array<NavItem>;
  siteSettings?: SiteSettingsOverride;
};

export type CommunityMetadata = {
  communityId?: number;
  domain: string;
  fanpost?: "hero" | "standard" | "none";
  volume: "high" | "low" | "feed-only";
  in5Game?: "standard" | "promo" | "mma";
  activeArticles: boolean;
  /** Comment count + Active Articles data source. Defaults to coral. */
  commentsProvider?: CommentsProvider;
  /** Article comment embed location. Defaults to brand resolver in comments.config.ts. */
  commentLocation?: CommentLocation;
  newsletterSlug?: string;
  teamName?: string;
  group?: string;
  blogName?: string;
  sport?: string;
  league?: string;
  thresholds?: {
    commentRecommended?: number;
    commentFlagged?: number;
    fanPostRecommended: number;
    fanPostFlagged: number;
    commentCallout: number;
  };
  coralId?: string;
  oneTrust?: {
    domainScript?: string;
  };
  feedOnlyConfig?: FeedOnlyConfig;
};

const useStagingCoralId =
  process.env.NEXT_PUBLIC_CORAL_ORIGIN?.includes("staging.coral");

const useOneTrustTestScript = !(process.env.NODE_ENV === "production");

export const SB_NATION_COMMUNITIES_METADATA: CommunityMetadata[] = [
  {
    communityId: SB_NATION_COMMUNITY_ID,
    domain: "sbnation.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "standard",
    activeArticles: true,
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 5,
      fanPostRecommended: 5,
      fanPostFlagged: 5,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "d6251933-b0cf-429f-926b-67ffffe769cd"
      : "00d34b0d-6971-4e94-b50b-a54564581790",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "1330cf01-01a6-40d0-a513-302b12256a6a-test"
        : "1330cf01-01a6-40d0-a513-302b12256a6a",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 12,
    domain: "azsnakepit.com",
    fanpost: "standard",
    volume: "low",
    in5Game: "promo",
    activeArticles: false,
    teamName: "Arizona Diamondbacks",
    group: "Baseball",
    blogName: "AZ Snake Pit",
    sport: "baseball",
    league: "mlb",
    thresholds: {
      commentRecommended: 4,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "c03576e4-b310-4b68-b463-6262c18c01d7"
      : "042d6d8d-5662-469a-bc40-9f7de272d829",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "8fa1a893-2c3f-4ff3-91cc-a83f3c67911f-test"
        : "8fa1a893-2c3f-4ff3-91cc-a83f3c67911f",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 26,
    domain: "batterypower.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "standard",
    activeArticles: true,
    newsletterSlug: "sbn_batterypower",
    teamName: "Atlanta Braves",
    group: "Baseball",
    blogName: "Battery Power",
    sport: "baseball",
    league: "mlb",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 3,
      fanPostRecommended: 3,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "eb51c5cb-505d-43bf-97d1-891a1e38f669"
      : "0469eca3-7011-463f-9510-1820b4a6d756",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "ddea16df-029a-41a2-9025-4023178c92e4-test"
        : "ddea16df-029a-41a2-9025-4023178c92e4",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 14,
    domain: "camdenchat.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "standard",
    activeArticles: true,
    teamName: "Baltimore Orioles",
    group: "Baseball",
    blogName: "Camden Chat",
    sport: "baseball",
    league: "mlb",
    thresholds: {
      commentRecommended: 4,
      commentFlagged: 4,
      fanPostRecommended: 2,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "6dc78351-a918-4bd2-bdac-30e6abf90ab1"
      : "7ca5a2a1-8853-4e14-85e1-6e3d1c2f2574",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "7180d9fb-3ad8-4aa1-8749-22a310064ec9-test"
        : "7180d9fb-3ad8-4aa1-8749-22a310064ec9",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 34,
    domain: "overthemonster.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: false,
    teamName: "Boston Red Sox",
    group: "Baseball",
    blogName: "Over the Monster",
    sport: "baseball",
    league: "mlb",
    thresholds: {
      commentRecommended: 4,
      commentFlagged: 2,
      fanPostRecommended: 2,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "d3f4085b-9cb8-47d2-823f-2116c292bbdd"
      : "4dde2e1c-149b-4e2e-ad29-1b4ec9633410",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "0b2274a9-02a1-4b15-a2a7-0117e1e2e433-test"
        : "0b2274a9-02a1-4b15-a2a7-0117e1e2e433",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 8,
    domain: "bleedcubbieblue.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "standard",
    activeArticles: true,
    teamName: "Chicago Cubs",
    group: "Baseball",
    blogName: "Bleed Cubbie Blue",
    sport: "baseball",
    league: "mlb",
    thresholds: {
      commentRecommended: 4,
      commentFlagged: 3,
      fanPostRecommended: 1,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "536e3f8e-4550-485d-b4b9-ddee967f96f5"
      : "feb9e321-9a49-4227-941b-abbe1f296c1e",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "521f9887-0a96-40d1-a5d5-c86d4898faf5-test"
        : "521f9887-0a96-40d1-a5d5-c86d4898faf5",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 20,
    domain: "southsidesox.com",
    fanpost: "hero",
    volume: "low",
    in5Game: "promo",
    activeArticles: false,
    teamName: "Chicago White Sox",
    group: "Baseball",
    blogName: "South Side Sox",
    sport: "baseball",
    league: "mlb",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "e0d3b202-9541-4f5c-969e-9326f53f456c"
      : "ddb95e4d-a224-4383-92e4-ec9eceed9133",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "4f253b8e-2ba1-46a3-bad7-afd1b175f015-test"
        : "4f253b8e-2ba1-46a3-bad7-afd1b175f015",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 25,
    domain: "redreporter.com",
    fanpost: "standard",
    volume: "low",
    in5Game: "promo",
    activeArticles: false,
    teamName: "Cincinnati Reds",
    group: "Baseball",
    blogName: "Red Reporter",
    sport: "baseball",
    league: "mlb",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 2,
      fanPostRecommended: 4,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "e1b76f22-bde3-499f-9427-e1cf0c033ce9"
      : "4baf85c8-1048-4b0e-a2ee-baa18565c510",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "868cde3f-161e-4bd4-90d5-d2417c245654-test"
        : "868cde3f-161e-4bd4-90d5-d2417c245654",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 15,
    domain: "coveringthecorner.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: true,
    teamName: "Cleveland Guardians",
    group: "Baseball",
    blogName: "Covering the Corner",
    sport: "baseball",
    league: "mlb",
    thresholds: {
      commentRecommended: 4,
      commentFlagged: 1,
      fanPostRecommended: 4,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "4bb06731-1a09-426f-862c-557c1f847434"
      : "121f904e-5fcf-4e5b-bab5-feae9070d7c5",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "efad71ce-7f71-406d-827f-e9b947b333c5-test"
        : "efad71ce-7f71-406d-827f-e9b947b333c5",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 19,
    domain: "purplerow.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: false,
    teamName: "Colorado Rockies",
    group: "Baseball",
    blogName: "Purple Row",
    sport: "baseball",
    league: "mlb",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 3,
      fanPostRecommended: 3,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "5b82c797-5114-4cfa-ab3e-05bee59285b5"
      : "c5057a30-096f-4f87-b701-88d8b52f9a41",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "7a857c46-93af-4215-addd-d90cf1381b69-test"
        : "7a857c46-93af-4215-addd-d90cf1381b69",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 23,
    domain: "blessyouboys.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: true,
    teamName: "Detroit Tigers",
    group: "Baseball",
    blogName: "Bless You Boys",
    sport: "baseball",
    league: "mlb",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 1,
      fanPostRecommended: 4,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "2dd5f8e2-6a8f-496d-ac20-74b1e1d1403d"
      : "977ebf93-578d-4129-a5c7-a47dcc87f132",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "5dfdf3ab-2e46-43c6-ac9e-60c080ee60bf-test"
        : "5dfdf3ab-2e46-43c6-ac9e-60c080ee60bf",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 21,
    domain: "crawfishboxes.com",
    fanpost: "standard",
    volume: "low",
    in5Game: "promo",
    activeArticles: false,
    teamName: "Houston Astros",
    group: "Baseball",
    blogName: "Crawfish Boxes",
    sport: "baseball",
    league: "mlb",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 1,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "04253061-68ce-4d9c-90fe-47c011f56538"
      : "0f6dc964-c5d0-4f09-8705-807ba9fc2b80",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "1ef87f8e-0fe2-4de9-b718-fdd5b6c0626b-test"
        : "1ef87f8e-0fe2-4de9-b718-fdd5b6c0626b",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 16,
    domain: "royalsreview.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: false,
    teamName: "Kansas City Royals",
    group: "Baseball",
    blogName: "Royals Review",
    sport: "baseball",
    league: "mlb",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 5,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "e5c0f819-b3fa-4239-bb46-de412a8a26f1"
      : "73299c04-af3f-4dbc-81f2-c59a39d3284c",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "f9c957c3-b030-4dc4-bcce-d851e9d1ca8d-test"
        : "f9c957c3-b030-4dc4-bcce-d851e9d1ca8d",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 32,
    domain: "truebluela.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "standard",
    activeArticles: true,
    newsletterSlug: "sbn_truebluela",
    teamName: "Los Angeles Dodgers",
    group: "Baseball",
    blogName: "True Blue LA",
    sport: "baseball",
    league: "mlb",
    thresholds: {
      commentRecommended: 4,
      commentFlagged: 2,
      fanPostRecommended: 2,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "6e488390-da09-4425-9e73-879e12086714"
      : "1c2154b5-1652-42d1-9110-0a07a563bbde",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "a5dcf101-8f6d-4fb2-affd-ab6fb8d763da-test"
        : "a5dcf101-8f6d-4fb2-affd-ab6fb8d763da",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 13,
    domain: "brewcrewball.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: false,
    teamName: "Milwaukee Brewers",
    group: "Baseball",
    blogName: "Brew Crew Ball",
    sport: "baseball",
    league: "mlb",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 3,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "cdfc1e8f-8ec0-4d51-902e-e074bac0669b"
      : "ed007c7e-d402-49c1-981d-988364437474",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "e19c6d10-b9c4-4954-9f3e-92a1092001a5-test"
        : "e19c6d10-b9c4-4954-9f3e-92a1092001a5",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 30,
    domain: "twinkietown.com",
    fanpost: "standard",
    volume: "low",
    in5Game: "promo",
    activeArticles: false,
    teamName: "Minnesota Twins",
    group: "Baseball",
    blogName: "Twinkie Town",
    sport: "baseball",
    league: "mlb",
    thresholds: {
      commentRecommended: 4,
      commentFlagged: 2,
      fanPostRecommended: 4,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "316c139d-2d6f-4243-94ca-fc0f43576cfa"
      : "a1d7d526-fb9c-4333-a547-f9eb5c660dd5",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "a304d68c-e59d-4548-af77-efd1814fde5d-test"
        : "a304d68c-e59d-4548-af77-efd1814fde5d",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 35,
    domain: "amazinavenue.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "standard",
    activeArticles: true,
    teamName: "New York Mets",
    group: "Baseball",
    blogName: "Amazin' Avenue",
    sport: "baseball",
    league: "mlb",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "51d7e67a-d476-4aa3-8196-978ca7a0c766"
      : "72f0763e-ed9c-4ffc-893e-327b2a02f6ff",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "8c05f6a7-9d02-4ec0-a6ae-ffa9def9f484-test"
        : "8c05f6a7-9d02-4ec0-a6ae-ffa9def9f484",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 11,
    domain: "pinstripealley.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "standard",
    activeArticles: true,
    newsletterSlug: "sbn_pinstripealley",
    teamName: "New York Yankees",
    group: "Baseball",
    blogName: "Pinstripe Alley",
    sport: "baseball",
    league: "mlb",
    thresholds: {
      commentRecommended: 4,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "f9333487-e9bd-46e6-b0ac-2873d3685e79"
      : "ee816e5b-2fdf-4794-a54f-b7f52b333617",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "1c9f663a-5f57-4e65-9f72-9cee324dcf55-test"
        : "1c9f663a-5f57-4e65-9f72-9cee324dcf55",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 1,
    domain: "athleticsnation.com",
    fanpost: "standard",
    volume: "low",
    in5Game: "promo",
    activeArticles: false,
    teamName: "Oakland Athletics",
    group: "Baseball",
    blogName: "Athletics Nation",
    sport: "baseball",
    league: "mlb",
    thresholds: {
      commentRecommended: 6,
      commentFlagged: 3,
      fanPostRecommended: 5,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "89a7dcb8-ed55-47f3-837a-44818a473f98"
      : "67087de1-bb89-4dbc-ba56-be9a2a83fd53",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "aef59549-2189-4728-b35b-3f1a311e80d8-test"
        : "aef59549-2189-4728-b35b-3f1a311e80d8",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 24,
    domain: "thegoodphight.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "standard",
    activeArticles: false,
    teamName: "Philadelphia Phillies",
    group: "Baseball",
    blogName: "The Good Phight",
    sport: "baseball",
    league: "mlb",
    thresholds: {
      commentRecommended: 4,
      commentFlagged: 1,
      fanPostRecommended: 4,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "55b5401c-6713-4357-a01c-d1dcd46d5a2b"
      : "c084f78b-e1e8-485f-bd8b-2bd2ba41a2ee",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "394bb9bc-56f0-4a9c-9567-e3134a7aefb6-test"
        : "394bb9bc-56f0-4a9c-9567-e3134a7aefb6",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 17,
    domain: "bucsdugout.com",
    fanpost: "hero",
    volume: "low",
    in5Game: "promo",
    activeArticles: false,
    teamName: "Pittsburgh Pirates",
    group: "Baseball",
    blogName: "Bucs Dugout",
    sport: "baseball",
    league: "mlb",
    thresholds: {
      commentRecommended: 4,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "38d1867c-86a4-4999-ba9f-625d77b771e4"
      : "4b57c872-3aaf-4926-ae6c-e091f3d5592f",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "d37cd252-946b-48e4-a4e7-122a933930f2-test"
        : "d37cd252-946b-48e4-a4e7-122a933930f2",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 33,
    domain: "gaslampball.com",
    fanpost: "standard",
    volume: "low",
    in5Game: "promo",
    activeArticles: false,
    teamName: "San Diego Padres",
    group: "Baseball",
    blogName: "Gaslamp Ball",
    sport: "baseball",
    league: "mlb",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 1,
      fanPostRecommended: 10,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "4cd65e66-ba14-469f-84ee-864b98adb447"
      : "2386b63d-ea47-4923-a814-61e55fa54f7c",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "46f51324-b550-476e-9b5e-4fce005b9bad-test"
        : "46f51324-b550-476e-9b5e-4fce005b9bad",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 4,
    domain: "mccoveychronicles.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: true,
    teamName: "San Francisco Giants",
    group: "Baseball",
    blogName: "McCovey Chronicles",
    sport: "baseball",
    league: "mlb",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 5,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "5c10a187-36c8-4b90-b697-28b6b5471c9e"
      : "3a7d58b8-5bed-4436-b197-16c3314ba190",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "dcf1385c-badf-4366-a303-66f8b20a19ff-test"
        : "dcf1385c-badf-4366-a303-66f8b20a19ff",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 29,
    domain: "lookoutlanding.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "standard",
    activeArticles: true,
    teamName: "Seattle Mariners",
    group: "Baseball",
    blogName: "Lookout Landing",
    sport: "baseball",
    league: "mlb",
    thresholds: {
      commentRecommended: 11,
      commentFlagged: 1,
      fanPostRecommended: 5,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "7afc33b0-71f5-4978-8507-61439e2d4920"
      : "502a8744-d381-44aa-95ae-0efd2f8f0788",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "64be187f-e2af-482b-ac35-89f431a53d69-test"
        : "64be187f-e2af-482b-ac35-89f431a53d69",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 5,
    domain: "vivaelbirdos.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: true,
    teamName: "St. Louis Cardinals",
    group: "Baseball",
    blogName: "Viva El Birdos",
    sport: "baseball",
    league: "mlb",
    thresholds: {
      commentRecommended: 6,
      commentFlagged: 1,
      fanPostRecommended: 5,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "50d13257-dbaf-4653-9b3c-38dbd5459951"
      : "87e74a5c-8221-43a9-b1da-92f175aaae28",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "6da69797-f1d3-42b1-9e9d-f6b4fa47724c-test"
        : "6da69797-f1d3-42b1-9e9d-f6b4fa47724c",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 31,
    domain: "draysbay.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: false,
    teamName: "Tampa Bay Rays",
    group: "Baseball",
    blogName: "DRaysBay",
    sport: "baseball",
    league: "mlb",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 3,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "7104a4fa-48e5-49a6-a8a8-9ebd89928582"
      : "9bb1a617-da22-409d-9213-275811220e1a",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "72e1c22f-3bf0-464b-a956-b387898c0944-test"
        : "72e1c22f-3bf0-464b-a956-b387898c0944",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 10,
    domain: "lonestarball.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: true,
    teamName: "Texas Rangers",
    group: "Baseball",
    blogName: "Lonestar Ball",
    sport: "baseball",
    league: "mlb",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 3,
      fanPostRecommended: 4,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "53bd5d56-6bc8-4b9a-ab9e-1caa8c87b0e2"
      : "8553ba43-eba6-41f6-971c-f2d0339cd44d",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "1d2cd5f6-8912-47f4-ba2a-8102c94a531a-test"
        : "1d2cd5f6-8912-47f4-ba2a-8102c94a531a",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 28,
    domain: "bluebirdbanter.com",
    fanpost: "standard",
    volume: "low",
    in5Game: "promo",
    activeArticles: false,
    teamName: "Toronto Blue Jays",
    group: "Baseball",
    blogName: "Bluebird Banter",
    sport: "baseball",
    league: "mlb",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "c1769640-6d06-48a9-a3e2-df3fc7de6472"
      : "e4fc6ff5-8f34-43be-95ba-24c6398999d0",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "4dbc413f-1a47-49c1-a770-dc3d76511973-test"
        : "4dbc413f-1a47-49c1-a770-dc3d76511973",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 27,
    domain: "federalbaseball.com",
    fanpost: "hero",
    volume: "low",
    in5Game: "promo",
    activeArticles: false,
    teamName: "Washington Nationals",
    group: "Baseball",
    blogName: "Federal Baseball",
    sport: "baseball",
    league: "mlb",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 3,
      fanPostFlagged: 5,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "0b4cd61d-b27e-41e1-9674-30c6ff118117"
      : "c06ed385-e4fb-499f-8b4b-cd19fffbd705",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "d5769ff4-ad02-490b-8322-52bc4c58bca1-test"
        : "d5769ff4-ad02-490b-8322-52bc4c58bca1",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 161,
    domain: "peachtreehoops.com",
    fanpost: "standard",
    volume: "low",
    in5Game: "promo",
    activeArticles: true,
    teamName: "Atlanta Hawks",
    group: "Basketball",
    blogName: "Peachtree Hoops",
    sport: "basketball",
    league: "nba",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 2,
      fanPostRecommended: 5,
      fanPostFlagged: 5,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "d1c49abd-a011-4687-bad1-fb9b1808100a"
      : "95905a06-d12b-4416-a30a-49cc00328866",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "59036539-ec1c-483f-a97c-a44a9aefa176-test"
        : "59036539-ec1c-483f-a97c-a44a9aefa176",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 175,
    domain: "celticsblog.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: true,
    newsletterSlug: "sbn_celticsblog",
    teamName: "Boston Celtics",
    group: "Basketball",
    blogName: "Celtics Blog",
    sport: "basketball",
    league: "nba",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "23303f38-45a7-4b9f-9e54-c4858c584852"
      : "112aa377-e27f-4b58-9156-5e5df25f39a2",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "ded6a202-ef8f-4815-8b43-baf37cd7d36d-test"
        : "ded6a202-ef8f-4815-8b43-baf37cd7d36d",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 276,
    domain: "netsdaily.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: true,
    teamName: "Brooklyn Nets",
    group: "Basketball",
    blogName: "Nets Daily",
    sport: "basketball",
    league: "nba",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 5,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "10153f19-79ac-40b4-a978-9c8b93a4f4bb"
      : "4803951c-e092-4e46-843b-cb98cbb7f907",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "bc2c0cdf-0964-4087-a4b9-4b94ab893530-test"
        : "bc2c0cdf-0964-4087-a4b9-4b94ab893530",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 158,
    domain: "fearthesword.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: true,
    teamName: "Cleveland Cavaliers",
    group: "Basketball",
    blogName: "Fear The Sword",
    sport: "basketball",
    league: "nba",
    thresholds: {
      commentRecommended: 6,
      commentFlagged: 2,
      fanPostRecommended: 2,
      fanPostFlagged: 5,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "afdd0e2b-2923-4395-9cc0-724b0264da05"
      : "230c7ae7-6aff-4192-a955-bc264139e922",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "fb0ae81a-a142-4390-a528-9e332b7161b0-test"
        : "fb0ae81a-a142-4390-a528-9e332b7161b0",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 38,
    domain: "mavsmoneyball.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: true,
    teamName: "Dallas Mavericks",
    group: "Basketball",
    blogName: "Mavs Moneyball",
    sport: "basketball",
    league: "nba",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 2,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "466c0d51-454e-44e9-90b1-64945cbf3a15"
      : "1c2cfe45-7372-477e-87e7-794086cc4fe2",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "38241c99-4afd-4eae-925a-7272eab35e53-test"
        : "38241c99-4afd-4eae-925a-7272eab35e53",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 222,
    domain: "detroitbadboys.com",
    fanpost: "standard",
    volume: "low",
    in5Game: "promo",
    activeArticles: true,
    teamName: "Detroit Pistons",
    group: "Basketball",
    blogName: "Detroit Bad Boys",
    sport: "basketball",
    league: "nba",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 3,
      fanPostRecommended: 3,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "473a07d5-ca07-4c37-a397-a18e8e34a02f"
      : "bc039cba-75dc-4ce6-9fee-4e29f9e87ae7",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "081ce0fa-47d9-49f4-851e-ec5fa9021e59-test"
        : "081ce0fa-47d9-49f4-851e-ec5fa9021e59",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 36,
    domain: "goldenstateofmind.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: true,
    newsletterSlug: "sbn_goldenstateofmind",
    teamName: "Golden State Warriors",
    group: "Basketball",
    blogName: "Golden State Of Mind",
    sport: "basketball",
    league: "nba",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 1,
      fanPostRecommended: 5,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "235a8443-1f2a-4712-8aca-7cb5abd6323d"
      : "78ac4866-d53b-4cdf-a93b-9d393ca858c2",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "074a1d2e-c208-4689-9b7d-64607ee3a894-test"
        : "074a1d2e-c208-4689-9b7d-64607ee3a894",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 172,
    domain: "thedreamshake.com",
    fanpost: "hero",
    volume: "low",
    in5Game: "promo",
    activeArticles: true,
    teamName: "Houston Rockets",
    group: "Basketball",
    blogName: "The Dream Shake",
    sport: "basketball",
    league: "nba",
    thresholds: {
      commentRecommended: 4,
      commentFlagged: 1,
      fanPostRecommended: 4,
      fanPostFlagged: 4,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "fa934968-c97f-4977-b223-e6c7b14dae8a"
      : "7aa7d1a9-03a3-4cde-a2dd-e2151a01187c",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "6a87054a-2af5-479d-acb7-54284eae12da-test"
        : "6a87054a-2af5-479d-acb7-54284eae12da",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 227,
    domain: "silverscreenandroll.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: true,
    newsletterSlug: "sbn_silverscreenandroll",
    teamName: "Los Angeles Lakers",
    group: "Basketball",
    blogName: "Silver Screen and Roll",
    sport: "basketball",
    league: "nba",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 3,
      fanPostRecommended: 3,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "c4652cc6-f3f7-485c-9f08-483d05cfcc17"
      : "cae686c8-6b4b-4858-8ca1-e305de74cc31",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "cf6045b6-9c4b-438f-8ad3-c06d556f3d18-test"
        : "cf6045b6-9c4b-438f-8ad3-c06d556f3d18",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 42,
    domain: "brewhoop.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: true,
    teamName: "Milwaukee Bucks",
    group: "Basketball",
    blogName: "Brew Hoop",
    sport: "basketball",
    league: "nba",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 3,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "86f30d7c-509a-4288-929c-916886f02f95"
      : "585eefa9-00f2-493b-8204-38aa43447265",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "a4731fc9-a666-4a7f-9e5e-ef730d503df7-test"
        : "a4731fc9-a666-4a7f-9e5e-ef730d503df7",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 51,
    domain: "canishoopus.com",
    fanpost: "hero",
    volume: "low",
    in5Game: "promo",
    activeArticles: true,
    teamName: "Minnesota Timberwolves",
    group: "Basketball",
    blogName: "Canis Hoopus",
    sport: "basketball",
    league: "nba",
    thresholds: {
      commentRecommended: 4,
      commentFlagged: 1,
      fanPostRecommended: 4,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "9f2999b5-b4df-4ff9-a88a-c8753a32dfb6"
      : "9d5c032f-8753-4695-af80-c95e387262a0",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "981239c4-b06f-47c4-aacd-40b414c80e39-test"
        : "981239c4-b06f-47c4-aacd-40b414c80e39",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 46,
    domain: "postingandtoasting.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: true,
    teamName: "New York Knicks",
    group: "Basketball",
    blogName: "Posting and Toasting",
    sport: "basketball",
    league: "nba",
    thresholds: {
      commentRecommended: 4,
      commentFlagged: 4,
      fanPostRecommended: 4,
      fanPostFlagged: 4,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "775d0f09-32be-4120-ab89-693984342bab"
      : "908c0092-886c-4b97-99ad-398f52c234a9",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "5b3e43b9-4e18-4eae-86f3-805125a38878-test"
        : "5b3e43b9-4e18-4eae-86f3-805125a38878",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 171,
    domain: "libertyballers.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: true,
    newsletterSlug: "sbn_libertyballers",
    teamName: "Philadelphia 76ers",
    group: "Basketball",
    blogName: "Liberty Ballers",
    sport: "basketball",
    league: "nba",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "76914804-afce-49ab-a641-a5fb5162e96c"
      : "25aa26de-4d9b-4718-8cbe-3f02a19fbf46",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "de9a5397-b1be-495e-92b9-5ee877be2c7b-test"
        : "de9a5397-b1be-495e-92b9-5ee877be2c7b",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 49,
    domain: "brightsideofthesun.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: true,
    newsletterSlug: "sbn_brightsideofthesun",
    teamName: "Phoenix Suns",
    group: "Basketball",
    blogName: "Bright Side Of The Sun",
    sport: "basketball",
    league: "nba",
    thresholds: {
      commentRecommended: 6,
      commentFlagged: 1,
      fanPostRecommended: 6,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "24c6102a-5e7d-4e02-9d80-6ba9c1c646c2"
      : "852f13ca-68f9-4ab3-8bda-8bc51cf00533",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "d5b0c18f-83e9-473f-b915-485e400c9c7b-test"
        : "d5b0c18f-83e9-473f-b915-485e400c9c7b",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 37,
    domain: "blazersedge.com",
    fanpost: "standard",
    volume: "high",
    in5Game: "promo",
    activeArticles: true,
    newsletterSlug: "sbn_blazersedge",
    teamName: "Portland Trail Blazers",
    group: "Basketball",
    blogName: "Blazer's Edge",
    sport: "basketball",
    league: "nba",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 1,
      fanPostRecommended: 5,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "bd66a225-50f2-4d3c-8bed-387be8acdb27"
      : "f39f93a6-4000-4e23-ace1-26ae8b240bee",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "e5dec34c-f2d0-4e91-8b06-11823494cbc3-test"
        : "e5dec34c-f2d0-4e91-8b06-11823494cbc3",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 39,
    domain: "poundingtherock.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: true,
    teamName: "San Antonio Spurs",
    group: "Basketball",
    blogName: "Pounding The Rock",
    sport: "basketball",
    league: "nba",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "d7744dc2-884c-4ef0-b1ef-073b478af988"
      : "e9d0b25c-df92-4c71-82b3-dd91b7df210c",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "d947e16a-7431-4ece-99b8-786c8787bbd5-test"
        : "d947e16a-7431-4ece-99b8-786c8787bbd5",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 230,
    domain: "raptorshq.com",
    fanpost: "standard",
    volume: "low",
    in5Game: "promo",
    activeArticles: false,
    commentsProvider: "cyclone",
    teamName: "Toronto Raptors",
    group: "Basketball",
    blogName: "Raptors HQ",
    sport: "basketball",
    league: "nba",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 5,
      fanPostRecommended: 5,
      fanPostFlagged: 5,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "818f7109-0ffe-4aa0-b108-8940cbebb524"
      : "88466a08-b4c2-4336-a7c8-dcf6a079e17a",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "a4a1c0f5-3cf3-4151-89a4-55523c534933-test"
        : "a4a1c0f5-3cf3-4151-89a4-55523c534933",
    },
  },
  {
    communityId: 113,
    domain: "slcdunk.com",
    fanpost: "hero",
    volume: "low",
    in5Game: "promo",
    activeArticles: true,
    teamName: "Utah Jazz",
    group: "Basketball",
    blogName: "SLC Dunk",
    sport: "basketball",
    league: "nba",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 1,
      fanPostRecommended: 1,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "7dfe9b38-66d0-492b-8a61-50691c25fd0a"
      : "16bb1d90-e822-4331-967b-fc92a0ffe018",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "1cfe6371-3ba5-48a4-981e-defdd6e5d87c-test"
        : "1cfe6371-3ba5-48a4-981e-defdd6e5d87c",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 48,
    domain: "bulletsforever.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: true,
    teamName: "Washington Wizards",
    group: "Basketball",
    blogName: "Bullets Forever",
    sport: "basketball",
    league: "nba",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 3,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "8c44dc1a-01d8-4cb4-9310-c689158cdd20"
      : "71c732b9-0ff2-45bf-beed-13e494f97fa0",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "fd2173f3-5aad-462c-aeb6-9a993087e4b0-test"
        : "fd2173f3-5aad-462c-aeb6-9a993087e4b0",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 248,
    domain: "swishappeal.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: false,
    teamName: "WNBA & Women's College Basketball",
    group: "Basketball",
    blogName: "Swish Appeal",
    sport: "basketball",
    league: "wnba",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 1,
      fanPostRecommended: 3,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "c2f7c8ba-f8e9-4e4d-b90c-6242aeec435c"
      : "9b895406-ad17-4f31-b30f-c7830ddfa89a",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "4a54fc5a-a522-439c-aa00-9332227f5b39-test"
        : "4a54fc5a-a522-439c-aa00-9332227f5b39",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 107,
    domain: "rollbamaroll.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: true,
    teamName: "Alabama Crimson Tide",
    group: "College",
    blogName: "Roll 'Bama Roll",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 2,
      fanPostRecommended: 5,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "4fedd498-7157-498c-a723-d621003a87fd"
      : "a4838b31-6a7a-46e0-8543-862a9158c45c",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "6dde3adf-0804-4af4-b92a-24e07dac6be6-test"
        : "6dde3adf-0804-4af4-b92a-24e07dac6be6",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 196,
    domain: "azdesertswarm.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: true,
    teamName: "Arizona Wildcats",
    group: "College",
    blogName: "Arizona Desert Swarm",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 3,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "5c89a5c5-b967-45c6-abfa-ff392ce2bc25"
      : "f4bbd709-c8bb-4ca0-8bfa-bba5bde2df79",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "40e8bd21-250f-4261-b31a-0728fb1da844-test"
        : "40e8bd21-250f-4261-b31a-0728fb1da844",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 160,
    domain: "offtackleempire.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: true,
    teamName: "Big Ten",
    group: "College",
    blogName: "Off Tackle Empire",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "0f32631f-a26b-4ecb-8bbf-89a50ac10965"
      : "1b0a207f-3e4e-4a43-988f-cb5afa0efc1a",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "8b176196-b851-4634-9e99-70b79d07bb73-test"
        : "8b176196-b851-4634-9e99-70b79d07bb73",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 342,
    domain: "btpowerhouse.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: false,
    commentsProvider: "cyclone",
    teamName: "Big Ten Basketball",
    group: "College",
    blogName: "BT Powerhouse",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "2af225d2-6d05-40ac-8f5a-cd324ed377c9"
      : "5fd842be-03b7-4ba7-9574-147120219133",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "19652749-6fa2-48d3-87d4-2830f46579a0-test"
        : "19652749-6fa2-48d3-87d4-2830f46579a0",
    },
  },
  {
    communityId: 242,
    domain: "bcinterruption.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: true,
    teamName: "Boston College Eagles",
    group: "College",
    blogName: "BC Interruption",
    sport: "college-basketball",
    league: "college-sports",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 1,
      fanPostRecommended: 5,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "15cc131f-c9af-4ddb-94a8-bcb3e02e27dd"
      : "abb4b371-1e7c-46f9-8c10-46981bc3f879",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "dc15825d-60f8-4db5-aa19-c2ebed05b7a7-test"
        : "dc15825d-60f8-4db5-aa19-c2ebed05b7a7",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 238,
    domain: "vanquishthefoe.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: false,
    commentsProvider: "cyclone",
    teamName: "BYU Cougars",
    group: "College",
    blogName: "Vanquish The Foe",
    sport: "college-basketball",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 3,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "16cbe4e2-e5aa-484a-8346-553ca403e0b8"
      : "7f1a694e-00bc-47fc-af17-c6a773392976",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "139c7533-9f81-4658-9fb2-8f6d0cc71680-test"
        : "139c7533-9f81-4658-9fb2-8f6d0cc71680",
    },
  },
  {
    communityId: 255,
    domain: "shakinthesouthland.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: true,
    teamName: "Clemson Tigers",
    group: "College",
    blogName: "Shakin The Southland",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 3,
      fanPostRecommended: 3,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "d2e3d8a0-05c8-4c1e-880f-30339da5d23c"
      : "375a6a42-95e0-45d8-8fde-a4682d096b15",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "8c4802ee-242c-4855-b16d-d86b1c8370a5-test"
        : "8c4802ee-242c-4855-b16d-d86b1c8370a5",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 156,
    domain: "ralphiereport.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: false,
    commentsProvider: "cyclone",
    teamName: "Colorado Buffaloes",
    group: "College",
    blogName: "The Ralphie Report",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 3,
      fanPostRecommended: 2,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "84b90c3e-13b7-4669-add2-731514607fd4"
      : "d69a0563-c4a9-410c-bbe1-20112d9a7395",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "103b9785-b389-43e5-86b5-8b81a567acc8-test"
        : "103b9785-b389-43e5-86b5-8b81a567acc8",
    },
  },
  {
    communityId: 417,
    domain: "dukebasketballreport.com",
    fanpost: "standard",
    volume: "high",
    activeArticles: false,
    teamName: "Duke Blue Devils",
    group: "College",
    blogName: "Duke Basketball Report",
    sport: "college-basketball",
    league: "college-sports",
    thresholds: {
      commentRecommended: 20,
      commentFlagged: 1,
      fanPostRecommended: 100,
      fanPostFlagged: 100,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "1b6e8d60-13f5-434a-bc17-dab891b4e990"
      : "bf279c8e-b6d9-4f50-8aa6-c0dd06f288b3",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "a7f93649-f657-4a58-a49a-7fb6aaded194-test"
        : "a7f93649-f657-4a58-a49a-7fb6aaded194",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 99,
    domain: "tomahawknation.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: true,
    teamName: "Florida State Seminoles",
    group: "College",
    blogName: "Tomahawk Nation",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 1,
      fanPostRecommended: 4,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "9a71023c-2574-42c0-be26-17072d74806b"
      : "5e4ba938-88e0-493c-80c7-0f2325a0e100",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "d6436354-2d3a-4fe4-9136-cc6e59b36f8c-test"
        : "d6436354-2d3a-4fe4-9136-cc6e59b36f8c",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 218,
    domain: "casualhoya.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: false,
    teamName: "Georgetown Hoyas",
    group: "College",
    blogName: "Casual Hoya",
    sport: "college-basketball",
    league: "college-sports",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 3,
      fanPostRecommended: 5,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "4a1913ff-0c67-4415-902c-59eb52a0f435"
      : "d67f66cc-2c95-4c18-903c-b506c4109209",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "db947eb5-d223-42e6-b849-dcbd83714651-test"
        : "db947eb5-d223-42e6-b849-dcbd83714651",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 114,
    domain: "dawgsports.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: false,
    teamName: "Georgia Bulldogs",
    group: "College",
    blogName: "Dawg Sports",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 4,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "f11f4ddc-7574-4f41-a4cf-81d98c5053ca"
      : "f7b59ca8-4b2f-41d5-b079-c7b4a9b9744b",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "c15f5af8-d471-4dbb-8415-cb6c5c2e7812-test"
        : "c15f5af8-d471-4dbb-8415-cb6c5c2e7812",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 234,
    domain: "fromtherumbleseat.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: false,
    commentsProvider: "cyclone",
    teamName: "Georgia Tech Yellow Jackets",
    group: "College",
    blogName: "From The Rumble Seat",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 2,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "7f2196bf-ea00-4e82-9e77-df5353929b20"
      : "7bc59e85-5a75-4a7b-b352-ef1e432f3610",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "ad5ffff0-88cc-40ed-88d1-ed4983fa7d2c-test"
        : "ad5ffff0-88cc-40ed-88d1-ed4983fa7d2c",
    },
  },
  {
    communityId: 187,
    domain: "slipperstillfits.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: true,
    teamName: "Gonzaga Bulldogs",
    group: "College",
    blogName: "The Slipper Still Fits",
    sport: "college-basketball",
    league: "college-sports",
    thresholds: {
      commentRecommended: 4,
      commentFlagged: 1,
      fanPostRecommended: 4,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "37562653-e5c0-4a4f-8a39-436c67186091"
      : "96180053-2ac6-494f-8b01-0667f400fcd5",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "2788d0ce-c3ca-4e6d-bdbb-70ee5dcf57ff-test"
        : "2788d0ce-c3ca-4e6d-bdbb-70ee5dcf57ff",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 407,
    domain: "thechampaignroom.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: false,
    teamName: "Illinois Fighting Illini",
    group: "College",
    blogName: "The Champaign Room",
    sport: "college-basketball",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "af17eae1-45ea-4dc4-b0d3-d2fce6df5038"
      : "8c4c0146-bccf-46a0-8fb2-264ef75ee99f",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "3ccccb94-6684-4577-bf00-f1335471a268-test"
        : "3ccccb94-6684-4577-bf00-f1335471a268",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 215,
    domain: "crimsonquarry.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: false,
    teamName: "Indiana Hoosiers",
    group: "College",
    blogName: "The Crimson Quarry",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "9dc46b3a-5e1e-483e-8579-d516a6eec608"
      : "a1cb8a5a-9c55-4d7c-abfa-6bbf6d42bbec",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "6fcc4397-9633-460e-97be-5bbe858b1925-test"
        : "6fcc4397-9633-460e-97be-5bbe858b1925",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 134,
    domain: "blackheartgoldpants.com",
    fanpost: "standard",
    volume: "high",
    activeArticles: true,
    teamName: "Iowa Hawkeyes",
    group: "College",
    blogName: "Black Heart Gold Pants",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 3,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "94bb4948-f46b-4aa6-a1a9-751b0a3d4a65"
      : "b3e44450-8267-4256-b87e-2f6cc88211ce",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "53306b6e-5263-4cf1-a65e-f6ae19bff421-test"
        : "53306b6e-5263-4cf1-a65e-f6ae19bff421",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 377,
    domain: "widerightnattylite.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: false,
    commentsProvider: "cyclone",
    teamName: "Iowa St. Cyclones",
    group: "College",
    blogName: "Wide Right & Natty Lite",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "79d91db9-65f5-4952-a4f8-09cd4ced6cb5"
      : "aed16601-1cd1-4d2c-89d4-555e5b49ccca",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "298478f5-b774-4a47-a21b-cbf349818076-test"
        : "298478f5-b774-4a47-a21b-cbf349818076",
    },
  },
  {
    communityId: 148,
    domain: "rockchalktalk.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: true,
    teamName: "Kansas Jayhawks",
    group: "College",
    blogName: "Rock Chalk Talk",
    sport: "college-basketball",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 3,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "14c2c175-9f18-4020-b6c6-734e0a41222d"
      : "c4eadc51-c95f-4dd8-b0f7-08ea04d88238",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "5d5609ec-e5e6-4a4d-ac92-216183928b8d-test"
        : "5d5609ec-e5e6-4a4d-ac92-216183928b8d",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 127,
    domain: "bringonthecats.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: false,
    teamName: "Kansas State Wildcats",
    group: "College",
    blogName: "Bring On The Cats",
    sport: "college-basketball",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "3878b7e6-8cc6-4a43-90d6-cbd8fbebd805"
      : "68bdc74e-d361-4878-83d9-ea68e644d682",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "2f826ed8-40d3-4d70-ac9a-260eb25eb3ad-test"
        : "2f826ed8-40d3-4d70-ac9a-260eb25eb3ad",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 109,
    domain: "aseaofblue.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: true,
    teamName: "Kentucky Wildcats",
    group: "College",
    blogName: "A Sea Of Blue",
    sport: "college-basketball",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 3,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "e4c941c4-fcec-44f8-bb08-abc58b4b2f24"
      : "4cbdec20-ffda-4a93-8369-8ae92a69aa35",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "37464d88-8431-49e9-b09a-111fdc688cc0-test"
        : "37464d88-8431-49e9-b09a-111fdc688cc0",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 144,
    domain: "cardchronicle.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: true,
    teamName: "Louisville Cardinals",
    group: "College",
    blogName: "Card Chronicle",
    sport: "college-basketball",
    league: "college-sports",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 2,
      fanPostRecommended: 20,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "01fa51fc-38aa-4313-a256-3df847eb9ff9"
      : "be7d93bb-312c-4ef2-863d-3913bcf3ee1b",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "96e88853-c22c-4a35-a7bb-75ed5f143f10-test"
        : "96e88853-c22c-4a35-a7bb-75ed5f143f10",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 101,
    domain: "andthevalleyshook.com",
    fanpost: "hero",
    volume: "low",
    activeArticles: false,
    teamName: "LSU Tigers",
    group: "College",
    blogName: "And The Valley Shook",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 2,
      fanPostRecommended: 5,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "9b16cb03-a625-4544-8f3c-63d5dc4c3f38"
      : "db51b8e3-6e36-435f-bcca-0ffeecd661b9",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "cfc459f7-5a31-4a20-80dc-6f24959b70ce-test"
        : "cfc459f7-5a31-4a20-80dc-6f24959b70ce",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 279,
    domain: "anonymouseagle.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: false,
    teamName: "Marquette Golden Eagles",
    group: "College",
    blogName: "Anonymous Eagle",
    sport: "college-basketball",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 3,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "486b2304-6bda-4f0f-82eb-618ee5cc62d4"
      : "dc4bbf47-ef63-4455-9b1f-f49af0647edf",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "ac236b84-565d-4bdf-a5b0-778de8c3c806-test"
        : "ac236b84-565d-4bdf-a5b0-778de8c3c806",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 151,
    domain: "testudotimes.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: true,
    teamName: "Maryland Terrapins",
    group: "College",
    blogName: "Testudo Times",
    sport: "college-basketball",
    league: "college-sports",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 2,
      fanPostRecommended: 7,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "5555dbe2-09ac-470a-8908-29f5319ff298"
      : "76845d8c-4f3b-4216-8cc9-183fbd8e2ca6",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "6ebbebc6-fd6d-481c-a914-e51c5f9ff2aa-test"
        : "6ebbebc6-fd6d-481c-a914-e51c5f9ff2aa",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 204,
    domain: "stateoftheu.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: false,
    teamName: "Miami Hurricanes",
    group: "College",
    blogName: "State of The U",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 2,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "c571cf11-b1a6-43f0-a0fb-53c54fbcd74c"
      : "6595b377-dfd2-4fbf-b6c7-13a54c004c99",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "5ebcada4-8d3a-4fa8-9360-fef94df58df8-test"
        : "5ebcada4-8d3a-4fa8-9360-fef94df58df8",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 224,
    domain: "theonlycolors.com",
    fanpost: "hero",
    volume: "low",
    activeArticles: false,
    teamName: "Michigan State Spartans",
    group: "College",
    blogName: "The Only Colors",
    sport: "college-basketball",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 3,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "36a23272-e6b6-465c-a54f-2e69ed9afada"
      : "80dcfaca-42bd-4baa-9ea7-a64e611992db",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "254a47cd-21a8-406b-a136-93310a0fb29e-test"
        : "254a47cd-21a8-406b-a136-93310a0fb29e",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 84,
    domain: "maizenbrew.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: true,
    newsletterSlug: "sbn_maizenbrew",
    teamName: "Michigan Wolverines",
    group: "College",
    blogName: "Maize n Brew",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 2,
      fanPostRecommended: 2,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "cfc03630-4520-4dee-a8a9-4c7347a5f2a9"
      : "2d9c6436-9b4e-4e19-a3fe-6d6e244b0ce6",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "a11231c6-657d-455b-9844-b1d62aa689e3-test"
        : "a11231c6-657d-455b-9844-b1d62aa689e3",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 231,
    domain: "midmajormadness.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: false,
    commentsProvider: "cyclone",
    teamName: "Mid Major Basketball",
    group: "College",
    blogName: "Mid-Major Madness",
    sport: "college-basketball",
    league: "college-sports",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 5,
      fanPostRecommended: 3,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "aea8bedd-9a02-4c54-beaa-5943f419a84f"
      : "8498261e-2072-4c2d-9202-8998d34cc863",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "a88bd0e0-fb91-4013-bcd9-134db2615bb6-test"
        : "a88bd0e0-fb91-4013-bcd9-134db2615bb6",
    },
  },
  {
    communityId: 318,
    domain: "hustlebelt.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: false,
    commentsProvider: "cyclone",
    teamName: "Mid-American Conference",
    group: "College",
    blogName: "Hustle Belt",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 4,
      commentFlagged: 1,
      fanPostRecommended: 2,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "d522d6a6-4612-4079-9ae3-a42f7c0dad36"
      : "f9ad58c1-df1c-48ed-b6ea-6879b1ac9374",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "913337e3-530a-4440-939d-b638a483a785-test"
        : "913337e3-530a-4440-939d-b638a483a785",
    },
  },
  {
    communityId: 153,
    domain: "thedailygopher.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: false,
    teamName: "Minnesota Golden Gophers",
    group: "College",
    blogName: "The Daily Gopher",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 2,
      fanPostRecommended: 2,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "aba63ea2-19ac-4f05-a3bf-3db165405bcc"
      : "a7f5ec7a-1caa-4ff8-8108-89c91421cf37",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "4f39ca94-4be7-4e19-9df4-94897110cb33-test"
        : "4f39ca94-4be7-4e19-9df4-94897110cb33",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 137,
    domain: "rockmnation.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: false,
    teamName: "Missouri Tigers",
    group: "College",
    blogName: "Rock M Nation",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 6,
      commentFlagged: 1,
      fanPostRecommended: 3,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "ebd69a1b-cd48-4739-9637-46595235d6b0"
      : "78ffd326-d6a8-46d0-9e54-368bb0326780",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "42e4866e-8dde-4e75-89b1-009b81342ec2-test"
        : "42e4866e-8dde-4e75-89b1-009b81342ec2",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 237,
    domain: "mwcconnection.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: false,
    teamName: "Mountain West Conference",
    group: "College",
    blogName: "MWC Connection",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 2,
      commentFlagged: 2,
      fanPostRecommended: 2,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "5aa62bff-b630-4443-b478-b4c05cb134cd"
      : "2871cfd0-2c77-4355-bd64-e4e8a9fd545d",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "49629171-d15e-4049-97a9-6d19df9262c1-test"
        : "49629171-d15e-4049-97a9-6d19df9262c1",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 232,
    domain: "backingthepack.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: true,
    teamName: "NC State Wolfpack",
    group: "College",
    blogName: "Backing The Pack",
    sport: "college-basketball",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 3,
      fanPostRecommended: 3,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "0aec9a6b-f93b-4049-a0e0-01147385834d"
      : "30b52530-2275-4ce5-b634-dcc258bb4842",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "0884da50-c375-47c6-a1b5-d1df90977f7a-test"
        : "0884da50-c375-47c6-a1b5-d1df90977f7a",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 217,
    domain: "bloggingthebracket.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: false,
    commentsProvider: "cyclone",
    teamName: "NCAA Basketball Bracketology",
    group: "College",
    blogName: "Blogging the Bracket",
    sport: "college-basketball",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 3,
      fanPostRecommended: 3,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "586b965b-9238-41f5-8514-4633f13d4f3d"
      : "c92e8c92-e854-48bc-be1b-7fdb448c2ecc",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "d8238124-3be8-4864-b792-a19aa738d292-test"
        : "d8238124-3be8-4864-b792-a19aa738d292",
    },
  },
  {
    communityId: 141,
    domain: "cornnation.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: false,
    teamName: "Nebraska Cornhuskers",
    group: "College",
    blogName: "Corn Nation",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 2,
      fanPostRecommended: 2,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "3b9e6a11-bab8-459a-bb7d-524ae35cd6a5"
      : "b9e99045-2fd2-4a18-a19f-a78d2f083026",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "bb18a263-7a57-435a-aba1-fa22ee32ba17-test"
        : "bb18a263-7a57-435a-aba1-fa22ee32ba17",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 142,
    domain: "tarheelblog.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: false,
    teamName: "North Carolina Tar Heels",
    group: "College",
    blogName: "Tar Heel Blog",
    sport: "college-basketball",
    league: "college-sports",
    thresholds: {
      commentRecommended: 4,
      commentFlagged: 2,
      fanPostRecommended: 2,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "35af941e-a6f1-4da4-800d-2524fcfd7f4c"
      : "40ad97f4-ba1b-4a40-9767-00434baaa7b8",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "40352dec-9f26-4702-895a-04313ea01601-test"
        : "40352dec-9f26-4702-895a-04313ea01601",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 246,
    domain: "insidenu.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: true,
    teamName: "Northwestern Wildcats",
    group: "College",
    blogName: "Inside NU",
    sport: "college-basketball",
    league: "college-sports",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 2,
      fanPostRecommended: 5,
      fanPostFlagged: 5,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "ddaa4a83-a8c2-4eb1-8b16-9e6eef31ee4d"
      : "627ce255-370b-428c-a745-74f0fc24d0e2",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "27deb7e3-b801-44cd-bb7b-91cb7d1648f1-test"
        : "27deb7e3-b801-44cd-bb7b-91cb7d1648f1",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 368,
    domain: "onefootdown.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: false,
    teamName: "Notre Dame Fighting Irish",
    group: "College",
    blogName: "One Foot Down",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 4,
      commentFlagged: 1,
      fanPostRecommended: 2,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "2887e830-ba97-428f-9b22-24ebea535b86"
      : "a4cab955-aca5-45eb-a30a-d9423e7a29be",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "e349938f-d6c5-4b03-9898-9394dc6f6495-test"
        : "e349938f-d6c5-4b03-9898-9394dc6f6495",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 400,
    domain: "landgrantholyland.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: false,
    teamName: "Ohio State Buckeyes",
    group: "College",
    blogName: "Land-Grant Holy Land",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "beb82c83-e37a-463f-bfa1-bc7e32ee0042"
      : "fab01233-0913-4093-b04d-98e4a06cabd5",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "ddc3d701-fa1f-4020-8ebd-aeccad937316-test"
        : "ddc3d701-fa1f-4020-8ebd-aeccad937316",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 211,
    domain: "redcuprebellion.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: false,
    commentsProvider: "cyclone",
    teamName: "Ole Miss Rebels",
    group: "College",
    blogName: "Red Cup Rebellion",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "8761ec76-aa34-4574-b30d-4a8951dc0102"
      : "3be6274a-c4a0-43e6-80d0-89bca0e212c1",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "8adfa040-5328-4d27-bd38-965bbfb00d9a-test"
        : "8adfa040-5328-4d27-bd38-965bbfb00d9a",
    },
  },
  {
    communityId: 145,
    domain: "addictedtoquack.com",
    fanpost: "hero",
    volume: "low",
    activeArticles: true,
    teamName: "Oregon Ducks",
    group: "College",
    blogName: "Addicted To Quack",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 5,
      fanPostFlagged: 5,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "9b9263de-d034-4e3a-8b1d-945cc2090350"
      : "c268d744-d7b0-416e-a611-e1a88d53470d",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "071b8cf1-dae0-4fb8-b910-6dc09d1d9995-test"
        : "071b8cf1-dae0-4fb8-b910-6dc09d1d9995",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 111,
    domain: "blackshoediaries.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: true,
    teamName: "Penn State Nittany Lions",
    group: "College",
    blogName: "Black Shoe Diaries",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 2,
      fanPostRecommended: 8,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "ec1b485c-7334-456e-b0af-03f637b824ee"
      : "40be2acf-f53a-4d96-9efd-1d456fa527c1",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "6b4afd5c-c35c-48b8-a8a8-614b0b3a1fe2-test"
        : "6b4afd5c-c35c-48b8-a8a8-614b0b3a1fe2",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 213,
    domain: "hammerandrails.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: true,
    teamName: "Purdue Boilermakers",
    group: "College",
    blogName: "Hammer and Rails",
    sport: "college-basketball",
    league: "college-sports",
    thresholds: {
      commentRecommended: 7,
      commentFlagged: 2,
      fanPostRecommended: 7,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "0dfe486d-1da7-43d6-b334-ef8cbd6ca973"
      : "e13474ea-6032-4028-ba28-153b97244291",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "bf65805c-cead-4d39-9bbe-633f6ea506e9-test"
        : "bf65805c-cead-4d39-9bbe-633f6ea506e9",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 448,
    domain: "underdogdynasty.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: false,
    commentsProvider: "cyclone",
    teamName: "Rest of the FBS",
    group: "College",
    blogName: "Underdog Dynasty",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 2,
      fanPostRecommended: 2,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "3cdf9948-cb0b-4fb2-a648-353e1bfae1f8"
      : "d32d2f63-d8be-4f49-9206-793fcf5d8e4b",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "b8eafc98-5d0e-4a34-b6d8-21e36a9875b4-test"
        : "b8eafc98-5d0e-4a34-b6d8-21e36a9875b4",
    },
  },
  {
    communityId: 275,
    domain: "onthebanks.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: false,
    teamName: "Rutgers Scarlet Knights",
    group: "College",
    blogName: "On The Banks",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 3,
      fanPostRecommended: 0,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "3417f69e-0623-4817-9a43-a95c55d2c83c"
      : "a8125dd4-6963-46a7-ae8e-d06f134f1585",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "613bcae0-7999-485f-873c-65fa2618a1e0-test"
        : "613bcae0-7999-485f-873c-65fa2618a1e0",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 341,
    domain: "rumbleinthegarden.com",
    fanpost: "hero",
    volume: "low",
    activeArticles: true,
    teamName: "St. John's Red Storm",
    group: "College",
    blogName: "Rumble In The Garden",
    sport: "college-basketball",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 2,
      fanPostRecommended: 2,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "6fc8e0f2-6cd3-4ac6-b617-1fa933b891b4"
      : "3a234c69-02b7-49c0-ad44-79398d53d5b9",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "d4d600c0-de14-4ee6-93c6-ae6d5d388cfd-test"
        : "d4d600c0-de14-4ee6-93c6-ae6d5d388cfd",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 168,
    domain: "nunesmagician.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: true,
    teamName: "Syracuse Orange",
    group: "College",
    blogName: "Troy Nunes Is An Absolute Magician",
    sport: "college-basketball",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 2,
      fanPostRecommended: 2,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "48f10164-2772-4607-90c6-c02f1c4bab4f"
      : "25ad89a1-4ebd-4d3f-ae92-c201fc7435e6",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "8ff1e29e-7e11-4e4e-8bd0-a26e805fe552-test"
        : "8ff1e29e-7e11-4e4e-8bd0-a26e805fe552",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 366,
    domain: "frogsowar.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: false,
    teamName: "TCU Horned Frogs",
    group: "College",
    blogName: "Frogs O' War",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 4,
      commentFlagged: 1,
      fanPostRecommended: 3,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "0ab73258-dd36-43f1-85aa-4e60c5c49e31"
      : "dd48c348-15f0-45e7-8f60-73aa05a366df",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "394ce606-6d7c-4419-a283-42745273d2a6-test"
        : "394ce606-6d7c-4419-a283-42745273d2a6",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 143,
    domain: "rockytoptalk.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: false,
    teamName: "Tennessee Volunteers",
    group: "College",
    blogName: "Rocky Top Talk",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 4,
      commentFlagged: 4,
      fanPostRecommended: 4,
      fanPostFlagged: 4,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "fbccf6e3-828d-4070-9df8-767f3ff9729e"
      : "690a430e-d993-409a-8064-0e622371ec86",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "986f5b28-7c30-4e16-83da-104ee5fcb7fc-test"
        : "986f5b28-7c30-4e16-83da-104ee5fcb7fc",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 402,
    domain: "goodbullhunting.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: false,
    teamName: "Texas A&M Aggies",
    group: "College",
    blogName: "Good Bull Hunting",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 2,
      fanPostRecommended: 2,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "1931cff3-a163-4453-9897-5c8fa4ac34fd"
      : "3b708c25-4cf9-4cc6-a9d0-8f96a2d4cd69",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "34526e16-25c7-4f4e-b4d8-1e7ed8777765-test"
        : "34526e16-25c7-4f4e-b4d8-1e7ed8777765",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 52,
    domain: "burntorangenation.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: true,
    teamName: "Texas Longhorns",
    group: "College",
    blogName: "Burnt Orange Nation",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 4,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "f7b6a0a9-fa26-485e-9286-655c0520fd8a"
      : "9a336f93-7998-49a8-b316-ddf724c589e5",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "387a7bef-a1f9-4a86-bbbd-764a019dd381-test"
        : "387a7bef-a1f9-4a86-bbbd-764a019dd381",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 749,
    domain: "blackandgoldbanneret.com",
    fanpost: "hero",
    volume: "low",
    activeArticles: false,
    commentsProvider: "cyclone",
    teamName: "UCF Knights",
    group: "College",
    blogName: "Black & Gold Banneret",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 5,
      fanPostRecommended: 5,
      fanPostFlagged: 5,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "1029eebb-b018-4fc9-9290-3239dc312cea"
      : "416950f9-1618-4d82-8a6a-e88287c9e000",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "7b702a5d-13c1-4a4f-9a85-9d482edb0c3a-test"
        : "7b702a5d-13c1-4a4f-9a85-9d482edb0c3a",
    },
  },
  {
    communityId: 270,
    domain: "theuconnblog.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: false,
    teamName: "UConn Huskies",
    group: "College",
    blogName: "The UConn Blog",
    sport: "college-basketball",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "baa657d2-39d5-43c8-8e07-e821ba68776d"
      : "a3fcee88-f090-4d85-a485-d25e5815c8db",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "29349b61-1d1a-4e48-9396-cc72d5bfbc5e-test"
        : "29349b61-1d1a-4e48-9396-cc72d5bfbc5e",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 259,
    domain: "anchorofgold.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: true,
    teamName: "Vanderbilt Commodores",
    group: "College",
    blogName: "Anchor Of Gold",
    sport: "college-basketball",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 3,
      fanPostRecommended: 2,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "d5d4d508-51ba-4ab4-b78b-2edaa998d9e7"
      : "fa57ed42-4739-4728-8a83-47e3288214fd",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "7fb18ad7-8914-4a44-b0d9-0321209af7d4-test"
        : "7fb18ad7-8914-4a44-b0d9-0321209af7d4",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 285,
    domain: "vuhoops.com",
    fanpost: "hero",
    volume: "low",
    activeArticles: false,
    teamName: "Villanova Wildcats",
    group: "College",
    blogName: "VU Hoops",
    sport: "college-basketball",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 2,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "0b83babc-d09a-432b-bcac-f76b3d61c64b"
      : "b576c012-3ae1-4257-89fd-8cee4be247f2",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "c228effe-c4ac-4215-9ae3-def79a27c314-test"
        : "c228effe-c4ac-4215-9ae3-def79a27c314",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 326,
    domain: "streakingthelawn.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: false,
    teamName: "Virginia Cavaliers",
    group: "College",
    blogName: "Streaking The Lawn",
    sport: "college-basketball",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 3,
      fanPostRecommended: 3,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "45896cd6-c833-4bf7-982d-231cba151d51"
      : "7367f472-72b4-421c-bad3-d4d9f54ef014",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "e98d2ab4-041e-4d50-8e4c-a2ea230dec25-test"
        : "e98d2ab4-041e-4d50-8e4c-a2ea230dec25",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 226,
    domain: "gobblercountry.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: false,
    teamName: "Virginia Tech Hokies",
    group: "College",
    blogName: "Gobbler Country",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 3,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "c8af5755-84bd-45fc-ac8a-a1f38d9a207e"
      : "4d87d871-c869-41de-bd02-a4277150f258",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "65feaa00-a0fb-4a98-927c-de659b9b1e08-test"
        : "65feaa00-a0fb-4a98-927c-de659b9b1e08",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 205,
    domain: "bloggersodear.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: false,
    teamName: "Wake Forest Demon Deacons",
    group: "College",
    blogName: "Blogger So Dear",
    sport: "college-basketball",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "90c60358-d6de-4e7e-b6c9-859bb0f6c5fe"
      : "5a9fbd18-1e9e-4c6f-a9cd-ae8ad984281c",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "63d6f9e5-cdfc-4e77-8380-ab3fc378937e-test"
        : "63d6f9e5-cdfc-4e77-8380-ab3fc378937e",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 159,
    domain: "uwdawgpound.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: true,
    teamName: "Washington Huskies",
    group: "College",
    blogName: "UW Dawg Pound",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 6,
      commentFlagged: 2,
      fanPostRecommended: 2,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "e3ca3b19-f588-4757-bbd1-c3c3db692571"
      : "ce219c21-5147-46f5-869f-610e715eebd3",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "0ed241c7-bb4f-4320-85f3-4bcc6704f2a5-test"
        : "0ed241c7-bb4f-4320-85f3-4bcc6704f2a5",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 157,
    domain: "cougcenter.com",
    fanpost: "hero",
    volume: "low",
    activeArticles: false,
    teamName: "Washington State Cougars",
    group: "College",
    blogName: "CougCenter",
    sport: "college-basketball",
    league: "college-sports",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "eb46ac8f-c5b6-4a22-8d9a-27eb5dd5b03b"
      : "be2d51b9-04ad-4e60-afed-8119b6273d8d",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "98a0b817-f71b-4b2c-8d82-f4fe6c748f27-test"
        : "98a0b817-f71b-4b2c-8d82-f4fe6c748f27",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 193,
    domain: "smokingmusket.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: false,
    teamName: "West Virginia Mountaineers",
    group: "College",
    blogName: "The Smoking Musket",
    sport: "college-basketball",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 2,
      fanPostRecommended: 2,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "f5fa2bac-af2b-4376-affc-8cf1367080c0"
      : "6b129f9c-3f28-49b6-8ed4-f5db65f0d2fa",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "0e4f456f-382d-414f-957d-467d3e6f0e22-test"
        : "0e4f456f-382d-414f-957d-467d3e6f0e22",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 207,
    domain: "buckys5thquarter.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: true,
    teamName: "Wisconsin Badgers",
    group: "College",
    blogName: "Bucky's 5th Quarter",
    sport: "college-football",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 3,
      fanPostRecommended: 3,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "11511a7f-fa13-4272-b0d6-f5e7aa9cfa37"
      : "3eb4a2a0-e463-4e7e-966b-032ded1b178c",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "b4fcc298-a2c4-4d80-9cd3-af593bb5fe7d-test"
        : "b4fcc298-a2c4-4d80-9cd3-af593bb5fe7d",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 361,
    domain: "bannersontheparkway.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: false,
    commentsProvider: "cyclone",
    teamName: "Xavier Musketeers",
    group: "College",
    blogName: "Banners On The Parkway",
    sport: "college-basketball",
    league: "college-sports",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 2,
      fanPostRecommended: 5,
      fanPostFlagged: 5,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "1bc96d10-4a90-46da-8438-6771d371a58f"
      : "d33bfd1c-ec25-464b-92dd-ac1b7801bb33",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "d683f645-0426-4d86-9da9-406298ca76bc-test"
        : "d683f645-0426-4d86-9da9-406298ca76bc",
    },
  },
  {
    communityId: 105,
    domain: "badlefthook.com",
    fanpost: "standard",
    volume: "high",
    activeArticles: true,
    newsletterSlug: "sbn_badlefthook",
    teamName: "Global Boxing News and Commentary",
    group: "Combat",
    blogName: "Bad Left Hook",
    sport: "boxing",
    league: "boxing",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 2,
      fanPostRecommended: 0,
      fanPostFlagged: 0,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "4b98fdef-1220-4798-935e-a4c122aba246"
      : "ea86ccfb-2e01-472d-84c2-1dd9ac0a3477",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "5381ee59-10f2-41a4-b59e-f13cd0fb4233-test"
        : "5381ee59-10f2-41a4-b59e-f13cd0fb4233",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 258,
    domain: "mmamania.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "mma",
    activeArticles: true,
    newsletterSlug: "sbn_mmamania",
    teamName: "Mixed Martial Arts",
    group: "Combat",
    sport: "mma",
    league: "mma",
    blogName: "MMA Mania",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 1,
      fanPostRecommended: 0,
      fanPostFlagged: 0,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "db233248-d7ea-4919-9910-30521c7d7514"
      : "ff6a9e98-5707-4071-8cd3-8f1e9ed387a1",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "2e8e0180-c1d7-42a5-b5cf-6a2fe581115e-test"
        : "2e8e0180-c1d7-42a5-b5cf-6a2fe581115e",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 244,
    domain: "cagesideseats.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "standard",
    activeArticles: true,
    newsletterSlug: "sbn_cagesideseats",
    teamName: "Pro Wrestling",
    group: "Combat",
    blogName: "Cageside Seats",
    sport: "professional-wrestling",
    league: "professional-wrestling",
    thresholds: {
      commentRecommended: 10,
      commentFlagged: 1,
      fanPostRecommended: 3,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "c1f64f5e-c536-42a8-94eb-97b5f56f3033"
      : "6f95e7bb-2ba8-4814-b42b-6af5540146d9",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "9225f88d-7225-49c9-9d55-37232a8d0ed2-test"
        : "9225f88d-7225-49c9-9d55-37232a8d0ed2",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 395,
    domain: "mmafighting.com",
    fanpost: "standard",
    volume: "high",
    in5Game: "standard",
    activeArticles: true,
    teamName: "UFC and MMA News and Results",
    group: "Combat",
    blogName: "MMA Fighting",
    sport: "mma",
    league: "mma",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 3,
      fanPostRecommended: 5,
      fanPostFlagged: 5,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "dce351fb-d19e-4cb4-942a-ded68d2a33d0"
      : "35cca751-c16e-4b2a-9538-38188ee72013",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "1471b745-2ebc-4a62-91de-91c70013e8d2-test"
        : "1471b745-2ebc-4a62-91de-91c70013e8d2",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 83,
    domain: "revengeofthebirds.com",
    fanpost: "hero",
    volume: "low",
    in5Game: "promo",
    activeArticles: true,
    teamName: "Arizona Cardinals",
    group: "NFL",
    blogName: "Revenge of the Birds",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 4,
      commentFlagged: 1,
      fanPostRecommended: 3,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "ab88c6e6-37a6-4423-a057-da523d943a3b"
      : "61d8ab34-6a9c-464c-a494-439b79d0bfa7",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "0a8ec4d9-7fc2-473f-b6da-4f63b3f9268f-test"
        : "0a8ec4d9-7fc2-473f-b6da-4f63b3f9268f",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 73,
    domain: "thefalcoholic.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: true,
    teamName: "Atlanta Falcons",
    group: "NFL",
    blogName: "The Falcoholic",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 1,
      fanPostRecommended: 5,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "4c589226-53b5-42d9-873e-b271f3c43a2b"
      : "4f9153b9-15c5-4cb9-a8e2-42fdbef40946",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "aee794bb-3665-4f9a-9dea-9ef465cf616d-test"
        : "aee794bb-3665-4f9a-9dea-9ef465cf616d",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 81,
    domain: "baltimorebeatdown.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: true,
    teamName: "Baltimore Ravens",
    group: "NFL",
    blogName: "Baltimore Beatdown",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "8366a6a8-ac90-426b-b54a-ebb7b41340bd"
      : "aa1d856b-b5bd-403d-86f2-17edeea74e2a",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "08e953ca-fbaa-487e-8d67-9d1d481d9d38-test"
        : "08e953ca-fbaa-487e-8d67-9d1d481d9d38",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 70,
    domain: "buffalorumblings.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "standard",
    activeArticles: true,
    teamName: "Buffalo Bills",
    group: "NFL",
    blogName: "Buffalo Rumblings",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 8,
      commentFlagged: 1,
      fanPostRecommended: 10,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "0067c31d-f8f7-4a88-992d-8e303e03f5ea"
      : "4cd94eaf-3876-416c-b546-48e36ee5671f",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "2c30640a-1dff-4502-8dc7-58b19468a919-test"
        : "2c30640a-1dff-4502-8dc7-58b19468a919",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 80,
    domain: "catscratchreader.com",
    fanpost: "hero",
    volume: "low",
    in5Game: "promo",
    activeArticles: true,
    teamName: "Carolina Panthers",
    group: "NFL",
    blogName: "Cat Scratch Reader",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 2,
      fanPostRecommended: 5,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "588ca52b-4d13-41be-af8b-64f0d4d36e4f"
      : "96d92dd0-a40e-4d72-a1f6-459d7418dfb1",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "82da94ea-21bb-453e-b15c-cecca31a7f29-test"
        : "82da94ea-21bb-453e-b15c-cecca31a7f29",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 58,
    domain: "windycitygridiron.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: true,
    teamName: "Chicago Bears",
    group: "NFL",
    blogName: "Windy City Gridiron",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 7,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "9bc8ca1c-905a-4d53-9b94-50ed67dc522b"
      : "61400a92-258a-4ca3-9942-099c1d5f801b",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "5ae374da-075d-4e5c-9af6-20cca2023c10-test"
        : "5ae374da-075d-4e5c-9af6-20cca2023c10",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 66,
    domain: "cincyjungle.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "standard",
    activeArticles: true,
    teamName: "Cincinnati Bengals",
    group: "NFL",
    blogName: "Cincy Jungle",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 1,
      fanPostRecommended: 3,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "94d2dbfe-1bf9-4dc7-9eac-63d371c8e3ab"
      : "dd796056-82fc-4ade-80e3-7c75241d372d",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "d949481d-76ed-4f9a-a573-528dbd493fcc-test"
        : "d949481d-76ed-4f9a-a573-528dbd493fcc",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 62,
    domain: "dawgsbynature.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "standard",
    activeArticles: true,
    teamName: "Cleveland Browns",
    group: "NFL",
    blogName: "Dawgs By Nature",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 2,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "5b849d7c-f1d6-45bb-bf07-9f22226f221d"
      : "94a89646-2dbb-431e-a6af-eedba2b63033",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "6e0988c1-a4cc-417d-b26d-6d0f76e77c92-test"
        : "6e0988c1-a4cc-417d-b26d-6d0f76e77c92",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 57,
    domain: "bloggingtheboys.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "standard",
    activeArticles: true,
    newsletterSlug: "sbn_bloggingtheboys",
    teamName: "Dallas Cowboys",
    group: "NFL",
    blogName: "Blogging The Boys",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 4,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "f5704c12-c0e7-43c7-a06f-f477d4f9b6a5"
      : "fc50df88-b511-451a-af10-47edecf1d739",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "2b3cd4bd-ec9d-4a2e-94b6-7175f2aab3d5-test"
        : "2b3cd4bd-ec9d-4a2e-94b6-7175f2aab3d5",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 55,
    domain: "milehighreport.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "standard",
    activeArticles: true,
    teamName: "Denver Broncos",
    group: "NFL",
    blogName: "Mile High Report",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 7,
      commentFlagged: 3,
      fanPostRecommended: 5,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "2e5a2d82-f059-40d1-8f6c-bf3fd3239198"
      : "e6921916-0fee-4e06-87a4-bd23a6db0619",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "c7ae0a1a-f220-4fbf-aa5c-ff9f042648dd-test"
        : "c7ae0a1a-f220-4fbf-aa5c-ff9f042648dd",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 63,
    domain: "prideofdetroit.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "standard",
    activeArticles: true,
    newsletterSlug: "sbn_prideofdetroit",
    teamName: "Detroit Lions",
    group: "NFL",
    blogName: "Pride of Detroit",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 4,
      commentFlagged: 1,
      fanPostRecommended: 4,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "ebd38caf-8d5f-4b03-bcc9-343ee81cf55f"
      : "f1b3b312-225a-490c-ac0b-0279480fb961",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "6e379e4a-1541-4c2d-af2a-145ef5dcbf89-test"
        : "6e379e4a-1541-4c2d-af2a-145ef5dcbf89",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 77,
    domain: "acmepackingcompany.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: true,
    teamName: "Green Bay Packers",
    group: "NFL",
    blogName: "Acme Packing Company",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 4,
      commentFlagged: 2,
      fanPostRecommended: 2,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "ad1eda99-5965-4b14-82ed-a2a99caad435"
      : "9b2642c2-ece5-4d56-a264-30e1d10e1f88",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "bc62e149-8a3a-474b-a5ed-6979c37aaec7-test"
        : "bc62e149-8a3a-474b-a5ed-6979c37aaec7",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 74,
    domain: "battleredblog.com",
    fanpost: "hero",
    volume: "low",
    in5Game: "promo",
    activeArticles: false,
    teamName: "Houston Texans",
    group: "NFL",
    blogName: "Battle Red Blog",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "f1221243-f1da-46d6-b29f-ca3bb1932e8f"
      : "b804ef0a-9723-41ca-84c3-2a29797deec7",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "52a5fd43-19f5-4d9a-81a2-10d0bbaaa70e-test"
        : "52a5fd43-19f5-4d9a-81a2-10d0bbaaa70e",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 56,
    domain: "stampedeblue.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: true,
    teamName: "Indianapolis Colts",
    group: "NFL",
    blogName: "Stampede Blue",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 2,
      fanPostRecommended: 5,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "3389cb1b-cad0-4631-a913-3865df67beb7"
      : "181c171e-c529-4e11-b604-45691c698aa2",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "b8cf6b73-a3a7-4f15-9a0b-bd054992ea71-test"
        : "b8cf6b73-a3a7-4f15-9a0b-bd054992ea71",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 54,
    domain: "bigcatcountry.com",
    fanpost: "hero",
    volume: "low",
    in5Game: "promo",
    activeArticles: true,
    teamName: "Jacksonville Jaguars",
    group: "NFL",
    blogName: "Big Cat Country",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 4,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "27c30f80-8d36-42f8-836d-0d52f4a96104"
      : "f781bf9c-bbfc-4c20-ae55-dc81da8eb9b5",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "3a54c6d7-31e9-498f-ab9f-f32b8ff2f1d5-test"
        : "3a54c6d7-31e9-498f-ab9f-f32b8ff2f1d5",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 59,
    domain: "arrowheadpride.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "standard",
    activeArticles: true,
    newsletterSlug: "sbn_arrowheadpride",
    teamName: "Kansas City Chiefs",
    group: "NFL",
    blogName: "Arrowhead Pride",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 1,
      fanPostRecommended: 10,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "52ad8fec-aa7d-42ae-9edc-500b4dc3269e"
      : "897927cf-9117-4440-b590-5369334a4b99",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "b1e323b3-2956-4e7e-9ce9-5d5cc33ec610-test"
        : "b1e323b3-2956-4e7e-9ce9-5d5cc33ec610",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 71,
    domain: "silverandblackpride.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "standard",
    activeArticles: true,
    teamName: "Las Vegas Raiders",
    group: "NFL",
    blogName: "Silver And Black Pride",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 3,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "d84ad534-5a9c-40f0-9f79-1b683190bd88"
      : "f1738efc-5686-4b0b-97e0-fb201f66dd55",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "4ac4bfe3-b473-4735-b228-7af37632e277-test"
        : "4ac4bfe3-b473-4735-b228-7af37632e277",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 167,
    domain: "boltsfromtheblue.com",
    fanpost: "hero",
    volume: "low",
    in5Game: "standard",
    activeArticles: true,
    teamName: "Los Angeles Chargers",
    group: "NFL",
    blogName: "Bolts From The Blue",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 3,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "54932590-c78d-4231-bb3c-a8d2c8be8df9"
      : "142fc6de-e0ea-4199-bb3f-91259ce795ce",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "2ef3faeb-a0b0-4c7c-aae9-9a3db15b9018-test"
        : "2ef3faeb-a0b0-4c7c-aae9-9a3db15b9018",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 75,
    domain: "turfshowtimes.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "standard",
    activeArticles: true,
    teamName: "Los Angeles Rams",
    group: "NFL",
    blogName: "Turf Show Times",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 3,
      fanPostRecommended: 2,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "5de1dbe0-493e-43d9-9e67-3672c40e51ea"
      : "ba81c921-3eb1-4e10-ae65-e263835f88fc",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "81665724-50cb-4f50-8cd1-f25e36bd928b-test"
        : "81665724-50cb-4f50-8cd1-f25e36bd928b",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 69,
    domain: "thephinsider.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: true,
    teamName: "Miami Dolphins",
    group: "NFL",
    blogName: "The Phinsider",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 1,
      fanPostRecommended: 4,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "ff9b7916-cf10-4c44-abdd-f9416cef68a3"
      : "8adca084-6d0f-4b76-99f7-aa639220430f",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "2ec61f24-0773-4022-a556-6ce5784b5553-test"
        : "2ec61f24-0773-4022-a556-6ce5784b5553",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 65,
    domain: "dailynorseman.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "standard",
    activeArticles: true,
    teamName: "Minnesota Vikings",
    group: "NFL",
    blogName: "Daily Norseman",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 2,
      fanPostRecommended: 2,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "4082e742-7d0f-40c5-8146-e9df5a2d6013"
      : "d4e14ca4-10d9-40f8-8948-a5f95750793c",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "c43e0520-8e33-4e86-8098-16b51e7b8b0d-test"
        : "c43e0520-8e33-4e86-8098-16b51e7b8b0d",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 82,
    domain: "patspulpit.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: true,
    teamName: "New England Patriots",
    group: "NFL",
    blogName: "Pats Pulpit",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 7,
      commentFlagged: 2,
      fanPostRecommended: 2,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "0d500cf6-4ed2-475b-a516-e267e8beb03f"
      : "ab7a8f8e-184d-40a5-a0ce-bed4ed06d00c",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "53a1576e-1c7e-461b-810a-14b70949f369-test"
        : "53a1576e-1c7e-461b-810a-14b70949f369",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 79,
    domain: "canalstreetchronicles.com",
    fanpost: "hero",
    volume: "low",
    in5Game: "promo",
    activeArticles: true,
    teamName: "New Orleans Saints",
    group: "NFL",
    blogName: "Canal Street Chronicles",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 3,
      fanPostRecommended: 7,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "a8c6118f-5d07-468b-af42-2ed3ffac13a5"
      : "b9e02b15-a5c0-4296-97a9-555aaff49ada",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "fa26c581-04f0-41f4-b5af-b91518c55ce3-test"
        : "fa26c581-04f0-41f4-b5af-b91518c55ce3",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 76,
    domain: "bigblueview.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "standard",
    activeArticles: true,
    newsletterSlug: "sbn_bigblueview",
    teamName: "New York Giants",
    group: "NFL",
    blogName: "Big Blue View",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 2,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "2fdb1028-7405-4d64-a735-d471acc07a11"
      : "41f0361a-2b3f-445c-a225-29ff2f30ee15",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "7b661266-2fa2-420b-8cf5-e2581f15ff63-test"
        : "7b661266-2fa2-420b-8cf5-e2581f15ff63",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 166,
    domain: "ganggreennation.com",
    fanpost: "hero",
    volume: "low",
    in5Game: "promo",
    activeArticles: true,
    teamName: "New York Jets",
    group: "NFL",
    blogName: "Gang Green Nation",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 3,
      fanPostRecommended: 5,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "b81c51ca-3d82-47e8-8002-1a498784b247"
      : "a5bd674f-9e70-4b16-a261-291c57c4a924",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "6e048218-fdcb-4723-a171-908de220e18a-test"
        : "6e048218-fdcb-4723-a171-908de220e18a",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 68,
    domain: "bleedinggreennation.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "standard",
    activeArticles: true,
    newsletterSlug: "sbn_bleedinggreennation",
    teamName: "Philadelphia Eagles",
    group: "NFL",
    blogName: "Bleeding Green Nation",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 2,
      fanPostRecommended: 5,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "6b0b00db-8fe5-4792-9eac-4bdff926fb02"
      : "1d62ad08-f417-4f8a-9f5f-a47dbda9c994",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "c5f47e8f-8da0-48ce-b2e0-358707a55178-test"
        : "c5f47e8f-8da0-48ce-b2e0-358707a55178",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 64,
    domain: "behindthesteelcurtain.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "standard",
    activeArticles: true,
    teamName: "Pittsburgh Steelers",
    group: "NFL",
    blogName: "Behind the Steel Curtain",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 2,
      fanPostRecommended: 8,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "8b2000d3-cf06-4a0d-9503-a5970218fcf0"
      : "48717fad-ddf6-4c7a-83aa-95fb901eaf0c",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "ef3c5abb-0e55-4243-8cec-e701b871acf3-test"
        : "ef3c5abb-0e55-4243-8cec-e701b871acf3",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 67,
    domain: "ninersnation.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "standard",
    activeArticles: true,
    newsletterSlug: "sbn_ninersnation",
    teamName: "San Francisco 49ers",
    group: "NFL",
    blogName: "Niners Nation",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 3,
      fanPostRecommended: 3,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "af7db057-6eea-49be-a087-44466cf744b7"
      : "1548aa06-b783-4cc3-9666-475504bd55db",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "424c1482-7782-4906-bf16-f1d4f22c5128-test"
        : "424c1482-7782-4906-bf16-f1d4f22c5128",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 60,
    domain: "fieldgulls.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "standard",
    activeArticles: true,
    teamName: "Seattle Seahawks",
    group: "NFL",
    blogName: "Field Gulls",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 12,
      commentFlagged: 1,
      fanPostRecommended: 4,
      fanPostFlagged: 4,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "1fabfdc3-d85e-4735-86ea-a89e5b993821"
      : "f607f692-1eea-47ca-80ca-b956f66a5424",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "d8dd9f96-ba9b-44a9-a67f-ebefe966cd1a-test"
        : "d8dd9f96-ba9b-44a9-a67f-ebefe966cd1a",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 78,
    domain: "bucsnation.com",
    fanpost: "hero",
    volume: "low",
    in5Game: "standard",
    activeArticles: false,
    teamName: "Tampa Bay Buccaneers",
    group: "NFL",
    blogName: "Bucs Nation",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "63ab2185-d8f7-4ba7-942f-29b4dc116178"
      : "659d3639-eb5e-4181-bc98-0f77ab58944b",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "b8276e58-0776-4b39-bc32-5009b3680c03-test"
        : "b8276e58-0776-4b39-bc32-5009b3680c03",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 72,
    domain: "musiccitymiracles.com",
    fanpost: "hero",
    volume: "low",
    in5Game: "promo",
    activeArticles: true,
    teamName: "Tennessee Titans",
    group: "NFL",
    blogName: "Music City Miracles",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 5,
      fanPostRecommended: 5,
      fanPostFlagged: 5,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "31345440-afdf-40e4-b348-28579e7b85cc"
      : "d7a6aee4-de26-4a9b-878b-8def7294a28e",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "fe337a35-fcc2-45df-8938-74efc635e73b-test"
        : "fe337a35-fcc2-45df-8938-74efc635e73b",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 61,
    domain: "hogshaven.com",
    fanpost: "hero",
    volume: "high",
    in5Game: "promo",
    activeArticles: true,
    teamName: "Washington Commanders",
    group: "NFL",
    blogName: "Hogs Haven",
    sport: "football",
    league: "nfl",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 3,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "e9839257-2423-4ff4-bb1f-92f0874edc61"
      : "525f0ef9-e985-479a-a0a9-6bd46be2db47",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "8da69ee7-b52a-4242-b9ea-5b9f05ac0323-test"
        : "8da69ee7-b52a-4242-b9ea-5b9f05ac0323",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 188,
    domain: "stanleycupofchowder.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: true,
    teamName: "Boston Bruins",
    group: "NHL",
    blogName: "Stanley Cup of Chowder",
    sport: "ice-hockey",
    league: "nhl",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 3,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "c4a1d43c-c7f4-4445-8e63-27be8ee949e2"
      : "3fa472db-5b51-43f3-940d-0848c17dc8bd",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "e9227bab-2710-410a-abb0-0cef85c9eef7-test"
        : "e9227bab-2710-410a-abb0-0cef85c9eef7",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 200,
    domain: "canescountry.com",
    fanpost: "hero",
    volume: "low",
    activeArticles: true,
    teamName: "Carolina Hurricanes",
    group: "NHL",
    blogName: "Canes Country",
    sport: "ice-hockey",
    league: "nhl",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "aa24192a-6cdc-4d40-8bee-4318566ddeb9"
      : "78cfee71-8166-41ce-ba0b-2a464dff4a6a",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "df4cc1d3-147f-48b3-8b90-00806980362f-test"
        : "df4cc1d3-147f-48b3-8b90-00806980362f",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 125,
    domain: "milehighhockey.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: true,
    teamName: "Colorado Avalanche",
    group: "NHL",
    blogName: "Mile High Hockey",
    sport: "ice-hockey",
    league: "nhl",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 2,
      fanPostRecommended: 4,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "30dc987f-2055-4e17-9320-6b3481a380f9"
      : "ecdeacb7-d0b4-441b-afee-d68e9fc8426f",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "cec8ef94-eaf2-4dd5-8c3e-d242c9d314c2-test"
        : "cec8ef94-eaf2-4dd5-8c3e-d242c9d314c2",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 176,
    domain: "allaboutthejersey.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: true,
    teamName: "New Jersey Devils",
    group: "NHL",
    blogName: "All About The Jersey",
    sport: "ice-hockey",
    league: "nhl",
    thresholds: {
      commentRecommended: 4,
      commentFlagged: 2,
      fanPostRecommended: 4,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "34ede76d-a3f6-40a3-8270-26be5ecb02cb"
      : "8ff643d7-b22b-4f91-9092-b8da6e53e70d",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "c2cad4e4-55d3-4d27-ae80-c713a6504a29-test"
        : "c2cad4e4-55d3-4d27-ae80-c713a6504a29",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 170,
    domain: "lighthousehockey.com",
    fanpost: "hero",
    volume: "low",
    activeArticles: true,
    teamName: "New York Islanders",
    group: "NHL",
    blogName: "Lighthouse Hockey",
    sport: "ice-hockey",
    league: "nhl",
    thresholds: {
      commentRecommended: 4,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "ca5f3f0f-5770-430e-8757-62240d8339e8"
      : "2665bb41-34ab-4b1c-8b7a-47754e77cb81",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "9aa7c004-742f-41f5-a1f6-c9d162a7dc61-test"
        : "9aa7c004-742f-41f5-a1f6-c9d162a7dc61",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 138,
    domain: "pensburgh.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: true,
    teamName: "Pittsburgh Penguins",
    group: "NHL",
    blogName: "PensBurgh",
    sport: "ice-hockey",
    league: "nhl",
    thresholds: {
      commentRecommended: 4,
      commentFlagged: 3,
      fanPostRecommended: 4,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "c6a8edaf-2602-4cf0-a4d8-12ee4119454e"
      : "8b69c68b-919b-467a-9c7c-9356faaffd83",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "1d885a8f-0e4c-4b6b-bd06-fdb3ce85ff0a-test"
        : "1d885a8f-0e4c-4b6b-bd06-fdb3ce85ff0a",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 378,
    domain: "acmilan.theoffside.com",
    fanpost: "standard",
    volume: "high",
    activeArticles: false,
    commentsProvider: "cyclone",
    teamName: "AC Milan",
    group: "Soccer",
    blogName: "The AC Milan Offside",
    sport: "soccer",
    league: "serie-a",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 3,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "b58ba8fe-d301-4ce9-ac75-48daf2b101ee"
      : "a64a489b-3423-4def-997c-1e6edd50ebe2",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "019122ba-86f6-708e-aba5-e0777ef4ae33-test"
        : "019122ba-86f6-708e-aba5-e0777ef4ae33",
    },
  },
  {
    communityId: 286,
    domain: "theshortfuse.sbnation.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: false,
    teamName: "Arsenal",
    group: "Soccer",
    blogName: "The Short Fuse",
    sport: "soccer",
    league: "epl",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 1,
      fanPostRecommended: 3,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "af27a788-582e-41f9-b5ba-bbffe1dc066e"
      : "748c7c48-fd1b-4107-a6f4-214d3af8488b",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "1330cf01-01a6-40d0-a513-302b12256a6a-test"
        : "1330cf01-01a6-40d0-a513-302b12256a6a",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 376,
    domain: "chiesaditotti.com",
    fanpost: "hero",
    volume: "low",
    activeArticles: true,
    teamName: "AS Roma",
    group: "Soccer",
    blogName: "Chiesa Di Totti",
    sport: "soccer",
    league: "serie-a",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 3,
      fanPostRecommended: 3,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "b0e9c4d8-1940-4500-b4ec-fdf9e4d0d28c"
      : "da1397f0-5dbb-4bde-9d99-4f780c4fce1c",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "fcd80778-b168-45d2-84cf-f68e54225dde-test"
        : "fcd80778-b168-45d2-84cf-f68e54225dde",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 562,
    domain: "intothecalderon.com",
    fanpost: "hero",
    volume: "low",
    activeArticles: false,
    teamName: "Atlético Madrid",
    group: "Soccer",
    blogName: "Into the Calderon",
    sport: "soccer",
    league: "la-liga",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 3,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "7c10e000-8fa1-459e-872a-e1b4f58aa034"
      : "6ad1e05e-0158-4f8b-9bce-28c38d94ee27",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "4b8915dd-839a-4232-86fd-072bf2558f62-test"
        : "4b8915dd-839a-4232-86fd-072bf2558f62",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 337,
    domain: "barcablaugranes.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: true,
    newsletterSlug: "sbn_barcablaugranes",
    teamName: "Barcelona",
    group: "Soccer",
    blogName: "Barca Blaugranes",
    sport: "soccer",
    league: "la-liga",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 3,
      fanPostRecommended: 3,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "151b45c7-a97c-4b76-b674-c336d0cfbf65"
      : "99bf1607-8a41-4ee1-a186-7142f3f526b1",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "26af10e8-ad35-4004-9d1c-a5e5c25eaa0f-test"
        : "26af10e8-ad35-4004-9d1c-a5e5c25eaa0f",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 398,
    domain: "bavarianfootballworks.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: true,
    newsletterSlug: "sbn_bavarianfootballworks",
    teamName: "Bayern Munich",
    group: "Soccer",
    blogName: "Bavarian Football Works",
    sport: "soccer",
    league: "bundesliga",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 1,
      fanPostRecommended: 3,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "d02a3152-217f-48c9-9c2b-54f60abb6bfa"
      : "460de1be-75c2-4f83-9f5a-908bce9e260a",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "8c01c0f2-b9f3-48a4-9008-f8ba17b8b9e8-test"
        : "8c01c0f2-b9f3-48a4-9008-f8ba17b8b9e8",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 621,
    domain: "fearthewall.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: false,
    teamName: "Borussia Dortmund",
    group: "Soccer",
    blogName: "Fear The Wall",
    sport: "soccer",
    league: "bundesliga",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 3,
      fanPostRecommended: 5,
      fanPostFlagged: 5,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "429b9ee2-69e2-4259-891d-7f7757de18b9"
      : "58e4aecb-9742-40d7-9a7c-6ab9e291099a",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "e250b3db-4a25-412a-b40b-2a39b7a68b34-test"
        : "e250b3db-4a25-412a-b40b-2a39b7a68b34",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 319,
    domain: "weaintgotnohistory.sbnation.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: true,
    newsletterSlug: "sbn_weaintgotnohistory",
    teamName: "Chelsea",
    group: "Soccer",
    blogName: "We Ain't Got No History",
    sport: "soccer",
    league: "epl",
    thresholds: {
      commentRecommended: 12,
      commentFlagged: 1,
      fanPostRecommended: 5,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "9d7f8107-e2d9-48e5-8eac-e9907846ef60"
      : "91bf9b3f-b9e4-484b-b96f-dcf9969b71ce",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "1330cf01-01a6-40d0-a513-302b12256a6a-test"
        : "1330cf01-01a6-40d0-a513-302b12256a6a",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 289,
    domain: "royalbluemersey.sbnation.com",
    fanpost: "standard",
    volume: "high",
    activeArticles: false,
    teamName: "Everton",
    group: "Soccer",
    blogName: "Royal Blue Mersey",
    sport: "soccer",
    league: "epl",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "a8d51b7a-21bf-46fa-a2c3-da4cea4052dc"
      : "cf871fa4-f681-4d35-bf7e-ba746a8b27f1",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "1330cf01-01a6-40d0-a513-302b12256a6a-test"
        : "1330cf01-01a6-40d0-a513-302b12256a6a",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 375,
    domain: "nevermanagealone.com",
    fanpost: "hero",
    volume: "low",
    activeArticles: false,
    teamName: "Fantasy Soccer",
    group: "Soccer",
    blogName: "Never Manage Alone",
    sport: "soccer",
    league: "soccer",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 3,
      fanPostRecommended: 3,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "34b97f0f-b82e-4406-a69d-0c0efa3b11eb"
      : "0427dd4d-c447-45dd-96f2-f970be28e151",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "6c9f7cec-5be8-42ba-bfe0-44837bf25345-test"
        : "6c9f7cec-5be8-42ba-bfe0-44837bf25345",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 403,
    domain: "violanation.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: false,
    teamName: "Fiorentina",
    group: "Soccer",
    blogName: "Viola Nation",
    sport: "soccer",
    league: "serie-a",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 1,
      fanPostRecommended: 5,
      fanPostFlagged: 5,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "2cf15f4f-7105-403b-a323-6429ea2394bd"
      : "5607a990-aa5a-49af-a891-82236c98bc3d",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "931488cf-354c-4870-a5a8-6590a4bcb27b-test"
        : "931488cf-354c-4870-a5a8-6590a4bcb27b",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 374,
    domain: "blackwhitereadallover.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: false,
    teamName: "Juventus",
    group: "Soccer",
    blogName: "Black & White & Read All Over",
    sport: "soccer",
    league: "serie-a",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 2,
      fanPostRecommended: 3,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "a842d108-e0ae-4221-8406-d7c391f81aad"
      : "f63239b7-5cae-494a-a6ab-16b37089d5c6",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "41b4dbc5-d95a-4e49-9076-c068ddbe407a-test"
        : "41b4dbc5-d95a-4e49-9076-c068ddbe407a",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 635,
    domain: "fosseposse.sbnation.com",
    fanpost: "none",
    volume: "low",
    activeArticles: false,
    teamName: "Leicester City",
    group: "Soccer",
    blogName: "Fosse Posse",
    sport: "soccer",
    league: "epl",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 3,
      fanPostRecommended: 3,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "daeb2cc2-0242-467e-843d-6feeab320c24"
      : "d7e4cf38-5c50-412e-a5c0-0f7eaf25f34e",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "1330cf01-01a6-40d0-a513-302b12256a6a-test"
        : "1330cf01-01a6-40d0-a513-302b12256a6a",
    },
  },
  {
    communityId: 373,
    domain: "liverpooloffside.sbnation.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: false,
    newsletterSlug: "sbn_liverpooloffside",
    teamName: "Liverpool",
    group: "Soccer",
    blogName: "The Liverpool Offside",
    sport: "soccer",
    league: "epl",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 2,
      fanPostRecommended: 5,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "a56a5851-be45-4a46-9f17-92349265b187"
      : "7bba5cf2-9596-4664-99ce-0148e8843a5f",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "1330cf01-01a6-40d0-a513-302b12256a6a-test"
        : "1330cf01-01a6-40d0-a513-302b12256a6a",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 194,
    domain: "bitterandblue.sbnation.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: false,
    commentsProvider: "cyclone",
    teamName: "Manchester City",
    group: "Soccer",
    blogName: "Bitter and Blue",
    sport: "soccer",
    league: "epl",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 3,
      fanPostRecommended: 3,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "0054a375-f927-4940-a12d-7fa3c753a659"
      : "b36a5721-584d-4bcb-99cb-408fa826e248",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "1330cf01-01a6-40d0-a513-302b12256a6a-test"
        : "1330cf01-01a6-40d0-a513-302b12256a6a",
    },
  },
  {
    communityId: 277,
    domain: "thebusbybabe.sbnation.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: false,
    teamName: "Manchester United",
    group: "Soccer",
    blogName: "The Busby Babe",
    sport: "soccer",
    league: "epl",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 3,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "37e33d36-e9b1-4eed-b059-212b728a2c0a"
      : "3590e849-66e8-4353-bd16-aa28c40e48bf",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "1330cf01-01a6-40d0-a513-302b12256a6a-test"
        : "1330cf01-01a6-40d0-a513-302b12256a6a",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 354,
    domain: "fmfstateofmind.com",
    fanpost: "standard",
    volume: "low",
    activeArticles: false,
    commentsProvider: "cyclone",
    teamName: "Mexico",
    group: "Soccer",
    blogName: "FMF State of Mind",
    sport: "soccer",
    league: "soccer",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 5,
      fanPostRecommended: 5,
      fanPostFlagged: 5,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "e2a62132-0c59-469f-abbf-b820c5493df2"
      : "217b147a-af2d-4251-998a-762a8134f0bc",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "daa6dfa2-691f-413a-b135-15722e19ab3e-test"
        : "daa6dfa2-691f-413a-b135-15722e19ab3e",
    },
  },
  {
    communityId: 346,
    domain: "cominghomenewcastle.sbnation.com",
    fanpost: "hero",
    volume: "low",
    activeArticles: false,
    commentsProvider: "cyclone",
    teamName: "Newcastle United",
    group: "Soccer",
    blogName: "Coming Home Newcastle",
    sport: "soccer",
    league: "epl",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 1,
      fanPostRecommended: 3,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "c2b5ee44-e14f-4a49-be74-0c4fcfdcd333"
      : "dee97eef-2d5e-460f-9b5f-8b4ea6a30072",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "1330cf01-01a6-40d0-a513-302b12256a6a-test"
        : "1330cf01-01a6-40d0-a513-302b12256a6a",
    },
  },
  {
    communityId: 392,
    domain: "thetilehurstend.sbnation.com",
    fanpost: "none",
    volume: "low",
    activeArticles: false,
    teamName: "Reading",
    group: "Soccer",
    blogName: "The Tilehurst End",
    sport: "soccer",
    league: "soccer",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 3,
      fanPostRecommended: 3,
      fanPostFlagged: 3,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "ed176349-da82-4499-8461-45d02fef9565"
      : "46d7a8cb-82ec-4e08-ba1b-26d5cf0d04aa",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "1330cf01-01a6-40d0-a513-302b12256a6a-test"
        : "1330cf01-01a6-40d0-a513-302b12256a6a",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 364,
    domain: "managingmadrid.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: true,
    newsletterSlug: "sbn_managingmadrid",
    teamName: "Real Madrid",
    group: "Soccer",
    blogName: "Managing Madrid",
    sport: "soccer",
    league: "la-liga",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 3,
      fanPostRecommended: 5,
      fanPostFlagged: 5,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "4238b5bd-23c4-40e1-9619-c231b8bfbc11"
      : "14bcc522-8623-45c8-9c80-8a6c60ca74af",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "76cfb142-036b-43a8-9213-40fa54ac1b80-test"
        : "76cfb142-036b-43a8-9213-40fa54ac1b80",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 350,
    domain: "rokerreport.sbnation.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: false,
    newsletterSlug: "sbn_rokerreport",
    teamName: "Sunderland",
    group: "Soccer",
    blogName: "Roker Report",
    sport: "soccer",
    league: "epl",
    thresholds: {
      commentRecommended: 3,
      commentFlagged: 3,
      fanPostRecommended: 5,
      fanPostFlagged: 5,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "08871901-00f8-414f-9272-3de64ec5eda2"
      : "7734c5ed-ef35-4154-8a36-471c325b8d4e",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "1330cf01-01a6-40d0-a513-302b12256a6a-test"
        : "1330cf01-01a6-40d0-a513-302b12256a6a",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 328,
    domain: "cartilagefreecaptain.sbnation.com",
    fanpost: "standard",
    volume: "high",
    activeArticles: true,
    teamName: "Tottenham Hotspur",
    group: "Soccer",
    blogName: "Cartilage Free Captain",
    sport: "soccer",
    league: "epl",
    thresholds: {
      commentRecommended: 11,
      commentFlagged: 3,
      fanPostRecommended: 5,
      fanPostFlagged: 5,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "275630e9-6096-48aa-9bf2-b44c2fc776da"
      : "722875ad-4ea7-460f-afc9-7b812fd6c673",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "1330cf01-01a6-40d0-a513-302b12256a6a-test"
        : "1330cf01-01a6-40d0-a513-302b12256a6a",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 404,
    domain: "starsandstripesfc.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: false,
    teamName: "US National Teams",
    group: "Soccer",
    blogName: "Stars and Stripes FC",
    sport: "soccer",
    league: "soccer",
    thresholds: {
      commentRecommended: 7,
      commentFlagged: 1,
      fanPostRecommended: 5,
      fanPostFlagged: 1,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "726c7ec5-2fbd-4a35-83a2-24406cf61db8"
      : "c53addd5-5f2d-41f6-8c26-3b630c01867f",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "8d56480e-daa1-4df5-b8e2-8a21cf54aa42-test"
        : "8d56480e-daa1-4df5-b8e2-8a21cf54aa42",
    },
    commentsProvider: "cyclone",
  },
  {
    communityId: 756,
    domain: "allforxi.com",
    fanpost: "hero",
    volume: "high",
    activeArticles: false,
    commentsProvider: "cyclone",
    teamName: "Women's Soccer",
    group: "Soccer",
    blogName: "All For XI",
    sport: "soccer",
    league: "nwsl",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 5,
      fanPostRecommended: 5,
      fanPostFlagged: 5,
      commentCallout: 75,
    },
    coralId: useStagingCoralId
      ? "dccaa08f-21ad-4ff5-908d-663734ac1d09"
      : "2b998ae0-4acd-46d5-ae42-bb71bd6db38d",
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "cfc85ff4-6e17-4ce1-a3ab-8548359eab0b-test"
        : "cfc85ff4-6e17-4ce1-a3ab-8548359eab0b",
    },
  },
  {
    communityId: 85,
    domain: "alligatorarmy.com",
    volume: "feed-only",
    activeArticles: false,
    teamName: "Florida Gators",
    group: "College Football",
    blogName: "Alligator Army",
    sport: "college-football",
    league: "ncaa",
    thresholds: {
      fanPostRecommended: 5,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "72be1086-c732-4089-bafb-d4c2e6589aca-test"
        : "72be1086-c732-4089-bafb-d4c2e6589aca",
    },
    feedOnlyConfig: {
      mainNav: [
        {
          title: "Community Guidelines",
          url: "https://www.sbnation.com/community-guidelines",
        },
        {
          title: "Newsletters",
          url: "https://www.sbnation.com/newsletters",
        },
        {
          title: "All Communities",
          url: "https://www.sbnation.com/communities",
        },
      ],
      siteSettings: {
        siteSlug: "alligatorarmy",
        siteTitle: "Alligator Army",
        siteDomain: "alligatorarmy.com",
        siteTagline:
          "Your best source for quality Florida Gators discussion from the fan perspective.",
        placeholderImage: {
          thumbnails: {
            horizontal: {
              url: "https://platform.sbnation.com/wp-content/uploads/sites/2/team-logos/logo-alligatorarmy.png?quality=90&strip=all&crop=10.75,0,78.5,100",
            },
            vertical: {
              url: "https://platform.sbnation.com/wp-content/uploads/sites/2/team-logos/logo-alligatorarmy.png?quality=90&strip=all&crop=29.066666666667,0,41.866666666667,100",
            },
            square: {
              url: "https://platform.sbnation.com/wp-content/uploads/sites/2/team-logos/logo-alligatorarmy.png?quality=90&strip=all&crop=23.833333333333,0,52.333333333333,100",
            },
            social: {
              url: "https://platform.sbnation.com/wp-content/uploads/sites/2/team-logos/logo-alligatorarmy.png?quality=90&strip=all&crop=0.021666666666668,0,99.956666666667,100",
            },
          },
        },
        siteLogo: {
          originalWidth: 1200,
          originalHeight: 628,
          thumbnails: {
            original: {
              url: "https://platform.sbnation.com/wp-content/uploads/sites/2/team-logos/logo-alligatorarmy.png?quality=90&strip=all&crop=0,0,100,100",
              cropCoordinates: { height: 100, width: 100 },
            },
            social: {
              url: "https://platform.sbnation.com/wp-content/uploads/sites/2/team-logos/logo-alligatorarmy.png?quality=90&strip=all&crop=0,0,100,100",
              cropCoordinates: { height: 100, width: 100 },
            },
          },
        },
        socialMediaLinks: [
          {
            platformName: "Facebook",
            url: "https://www.facebook.com/alligatorarmy/",
          },
        ],
        seo: {
          defaultSchema: {
            title: "Alligator Army, a Florida Gators Community",
            facebookAppId: "249141081161",
            googleSiteVerification:
              "-FHseA62RLF6_ZknZtli15hrKvvNysuYB4fWGmxg04s",
          },
          newsMediaOrganizationSchema: {
            description:
              "Your best source for quality Florida Gators discussion from the fan perspective.",
            foundingDate: null,
            founders: [],
            knowsAbout: [],
            diversityPolicyUrl: "https://diversity.voxmedia.com/",
            ethicsPolicyUrl:
              "https://www.sbnation.com/pages/guiding-principles",
            mastheadUrl: null,
            wikipediaUrl: null,
            linkedInUrl: null,
          },
        },
      },
    },
  },
  {
    communityId: 152,
    domain: "houseofsparky.com",
    volume: "feed-only",
    activeArticles: false,
    teamName: "Arizona State Athletics",
    blogName: "House of Sparky",
    league: "ncaa",
    thresholds: {
      commentRecommended: 5,
      commentFlagged: 2,
      fanPostRecommended: 5,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "8f253ed0-72c0-447d-bd2c-78fce01a6ff0-test"
        : "8f253ed0-72c0-447d-bd2c-78fce01a6ff0",
    },
    feedOnlyConfig: {
      mainNav: [
        {
          title: "Community Guidelines",
          url: "https://www.sbnation.com/community-guidelines",
        },
        {
          title: "Newsletters",
          url: "https://www.sbnation.com/newsletters",
        },
        {
          title: "All Communities",
          url: "https://www.sbnation.com/communities",
        },
      ],
      siteSettings: {
        siteSlug: "houseofsparky",
        siteTitle: "House of Sparky",
        siteDomain: "houseofsparky.com",
        siteTagline:
          "Your best source for quality Arizona State Sun Devils discussion from the fan perspective.",
        placeholderImage: {
          thumbnails: {
            horizontal: {
              url: "https://platform.sbnation.com/wp-content/uploads/sites/2/team-logos/logo-houseofsparky.png?quality=90&strip=all&crop=10.75,0,78.5,100",
            },
            vertical: {
              url: "https://platform.sbnation.com/wp-content/uploads/sites/2/team-logos/logo-houseofsparky.png?quality=90&strip=all&crop=29.066666666667,0,41.866666666667,100",
            },
            square: {
              url: "https://platform.sbnation.com/wp-content/uploads/sites/2/team-logos/logo-houseofsparky.png?quality=90&strip=all&crop=23.833333333333,0,52.333333333333,100",
            },
            social: {
              url: "https://platform.sbnation.com/wp-content/uploads/sites/2/team-logos/logo-houseofsparky.png?quality=90&strip=all&crop=0.021666666666668,0,99.956666666667,100",
            },
          },
        },
        siteLogo: {
          originalWidth: 1200,
          originalHeight: 628,
          thumbnails: {
            original: {
              url: "https://platform.sbnation.com/wp-content/uploads/sites/2/team-logos/logo-houseofsparky.png?quality=90&strip=all&crop=0,0,100,100",
              cropCoordinates: { height: 100, width: 100 },
            },
            social: {
              url: "https://platform.sbnation.com/wp-content/uploads/sites/2/team-logos/logo-houseofsparky.png?quality=90&strip=all&crop=0,0,100,100",
              cropCoordinates: { height: 100, width: 100 },
            },
          },
        },
        socialMediaLinks: [
          {
            platformName: "Facebook",
            url: "https://www.facebook.com/houseofsparky/",
          },
        ],
        seo: {
          defaultSchema: {
            title: "House of Sparky, an Arizona State Sun Devils Community",
            facebookAppId: "249141081161",
            googleSiteVerification:
              "-FHseA62RLF6_ZknZtli15hrKvvNysuYB4fWGmxg04s",
          },
          newsMediaOrganizationSchema: {
            description:
              "Your best source for quality Arizona State Sun Devils discussion from the fan perspective.",
            foundingDate: null,
            founders: [],
            knowsAbout: [],
            diversityPolicyUrl: "https://diversity.voxmedia.com/",
            ethicsPolicyUrl:
              "https://www.sbnation.com/pages/guiding-principles",
            mastheadUrl: null,
            wikipediaUrl: null,
            linkedInUrl: null,
          },
        },
      },
    },
  },
  {
    communityId: 106,
    domain: "bruinsnation.com",
    volume: "feed-only",
    activeArticles: false,
    teamName: "UCLA Bruins",
    blogName: "Bruins Nation",
    league: "ncaa",
    thresholds: {
      fanPostRecommended: 5,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "05f9f228-ceaa-4191-9852-92c6bd6117bd-test"
        : "05f9f228-ceaa-4191-9852-92c6bd6117bd",
    },
    feedOnlyConfig: {
      mainNav: [
        {
          title: "Community Guidelines",
          url: "https://www.sbnation.com/community-guidelines",
        },
        {
          title: "Newsletters",
          url: "https://www.sbnation.com/newsletters",
        },
        {
          title: "All Communities",
          url: "https://www.sbnation.com/communities",
        },
      ],
      siteSettings: {
        siteSlug: "bruinsnation",
        siteTitle: "Bruins Nation",
        siteDomain: "bruinsnation.com",
        siteTagline:
          "Your best source for quality UCLA Bruins discussion from the fan perspective.",
        placeholderImage: {
          thumbnails: {
            horizontal: {
              url: "https://platform.sbnation.com/wp-content/uploads/sites/2/team-logos/logo-bruinsnation.png?quality=90&strip=all&crop=10.75,0,78.5,100",
            },
            vertical: {
              url: "https://platform.sbnation.com/wp-content/uploads/sites/2/team-logos/logo-bruinsnation.png?quality=90&strip=all&crop=29.066666666667,0,41.866666666667,100",
            },
            square: {
              url: "https://platform.sbnation.com/wp-content/uploads/sites/2/team-logos/logo-bruinsnation.png?quality=90&strip=all&crop=23.833333333333,0,52.333333333333,100",
            },
            social: {
              url: "https://platform.sbnation.com/wp-content/uploads/sites/2/team-logos/logo-bruinsnation.png?quality=90&strip=all&crop=0.021666666666668,0,99.956666666667,100",
            },
          },
        },
        siteLogo: {
          originalWidth: 1200,
          originalHeight: 628,
          thumbnails: {
            original: {
              url: "https://platform.sbnation.com/wp-content/uploads/sites/2/team-logos/logo-bruinsnation.png?quality=90&strip=all&crop=0,0,100,100",
              cropCoordinates: { height: 100, width: 100 },
            },
            social: {
              url: "https://platform.sbnation.com/wp-content/uploads/sites/2/team-logos/logo-bruinsnation.png?quality=90&strip=all&crop=0,0,100,100",
              cropCoordinates: { height: 100, width: 100 },
            },
          },
        },
        socialMediaLinks: [
          {
            platformName: "Facebook",
            url: "https://www.facebook.com/bruinsnation/",
          },
        ],
        seo: {
          defaultSchema: {
            title: "Bruins Nation, a UCLA Bruins Community",
            facebookAppId: "249141081161",
            googleSiteVerification:
              "-FHseA62RLF6_ZknZtli15hrKvvNysuYB4fWGmxg04s",
          },
          newsMediaOrganizationSchema: {
            description:
              "Your best source for quality UCLA Bruins discussion from the fan perspective.",
            foundingDate: null,
            founders: [],
            knowsAbout: [],
            diversityPolicyUrl: "https://diversity.voxmedia.com/",
            ethicsPolicyUrl:
              "https://www.sbnation.com/pages/guiding-principles",
            mastheadUrl: null,
            wikipediaUrl: null,
            linkedInUrl: null,
          },
        },
      },
    },
  },
  {
    communityId: 47,
    domain: "clipsnation.com",
    volume: "feed-only",
    activeArticles: false,
    teamName: "Los Angeles Clippers",
    blogName: "Clips Nation",
    league: "nba",
    thresholds: {
      fanPostRecommended: 5,
      fanPostFlagged: 2,
      commentCallout: 75,
    },
    oneTrust: {
      domainScript: useOneTrustTestScript
        ? "ab1ad0d5-8d05-4bd2-9c02-e768d8443d00-test"
        : "ab1ad0d5-8d05-4bd2-9c02-e768d8443d00",
    },
    feedOnlyConfig: {
      mainNav: [
        {
          title: "Community Guidelines",
          url: "https://www.sbnation.com/community-guidelines",
        },
        {
          title: "Newsletters",
          url: "https://www.sbnation.com/newsletters",
        },
        {
          title: "All Communities",
          url: "https://www.sbnation.com/communities",
        },
      ],
      siteSettings: {
        siteSlug: "clipsnation",
        siteTitle: "Clips Nation",
        siteDomain: "clipsnation.com",
        siteTagline:
          "Your best source for quality Los Angeles Clippers discussion from the fan perspective.",
        placeholderImage: {
          thumbnails: {
            horizontal: {
              url: "https://platform.sbnation.com/wp-content/uploads/sites/2/team-logos/logo-clipsnation.png?quality=90&strip=all&crop=10.75,0,78.5,100",
            },
            vertical: {
              url: "https://platform.sbnation.com/wp-content/uploads/sites/2/team-logos/logo-clipsnation.png?quality=90&strip=all&crop=29.066666666667,0,41.866666666667,100",
            },
            square: {
              url: "https://platform.sbnation.com/wp-content/uploads/sites/2/team-logos/logo-clipsnation.png?quality=90&strip=all&crop=23.833333333333,0,52.333333333333,100",
            },
            social: {
              url: "https://platform.sbnation.com/wp-content/uploads/sites/2/team-logos/logo-clipsnation.png?quality=90&strip=all&crop=0.021666666666668,0,99.956666666667,100",
            },
          },
        },
        siteLogo: {
          originalWidth: 1200,
          originalHeight: 628,
          thumbnails: {
            original: {
              url: "https://platform.sbnation.com/wp-content/uploads/sites/2/team-logos/logo-clipsnation.png?quality=90&strip=all&crop=0,0,100,100",
              cropCoordinates: { height: 100, width: 100 },
            },
            social: {
              url: "https://platform.sbnation.com/wp-content/uploads/sites/2/team-logos/logo-clipsnation.png?quality=90&strip=all&crop=0,0,100,100",
              cropCoordinates: { height: 100, width: 100 },
            },
          },
        },
        socialMediaLinks: [
          {
            platformName: "Facebook",
            url: "https://www.facebook.com/clipsnation/",
          },
        ],
        seo: {
          defaultSchema: {
            title: "Clips Nation, a Los Angeles Clippers Community",
            facebookAppId: "249141081161",
            googleSiteVerification:
              "-FHseA62RLF6_ZknZtli15hrKvvNysuYB4fWGmxg04s",
          },
          newsMediaOrganizationSchema: {
            description:
              "Your best source for quality Los Angeles Clippers discussion from the fan perspective.",
            foundingDate: null,
            founders: [],
            knowsAbout: [],
            diversityPolicyUrl: "https://diversity.voxmedia.com/",
            ethicsPolicyUrl:
              "https://www.sbnation.com/pages/guiding-principles",
            mastheadUrl: null,
            wikipediaUrl: null,
            linkedInUrl: null,
          },
        },
      },
    },
  },
];

/**
 * Returns the community metadata object for the given communityId.
 *
 * @param {number} communityId - The ID of the community to find.
 * @returns {CommunityMetadata | undefined} The metadata object if found, otherwise undefined.
 */
export function getCommunityMetadata(
  communityId: number,
  throwOnError = true,
): CommunityMetadata | undefined {
  const communityMetadata = SB_NATION_COMMUNITIES_METADATA.find(
    (community) => community.communityId === communityId,
  );

  if (!communityMetadata && throwOnError) {
    throw new Error(
      `Community metadata not found for communityId: ${communityId}`,
    );
  }

  return communityMetadata;
}
